<template>
    <div role="application" aria-label="Aplicativo de rastreamento de ônibus">
        <!-- Skip to main content link for keyboard users -->
        <a href="#main-content" class="sr-only sr-only-focusable">Pular para o conteúdo principal</a>
        
        <transition name="splash-slide">
            <SplashScreen v-if="showSplash" />
        </transition>

        <v-dialog v-model="showPWAInstall" persistent :retain-focus="false" :overlay="false" :max-width="400"
            :content-class="'pwa-install-dialog'"
            role="dialog"
            aria-labelledby="pwa-dialog-title"
            aria-describedby="pwa-dialog-desc">
            <v-card class="pwa-install-card">
                <v-card-text class="pa-4">
                    <v-row align="center" no-gutters>
                        <v-col cols="2" class="text-center">
                            <v-icon large color="primary">mdi-download</v-icon>
                        </v-col>
                        <v-col cols="8" class="pl-3">
                            <div id="pwa-dialog-title" class="text-h6 font-weight-bold">Instalar App</div>
                            <div id="pwa-dialog-desc" class="text-body-2 text-grey-darken-1">
                                <span v-if="isIOS">
                                    {{ isSafari ? $t('consultationPassenger.installAppSubtitleIOS') :
                                        $t('consultationPassenger.installAppSubtitleSafari') }}
                                </span>
                                <span v-else>
                                    {{ $t('consultationPassenger.installAppSubtitle') }}
                                </span>
                            </div>
                        </v-col>
                        <v-col cols="2" class="text-center">
                            <v-btn icon small @click="closePWAInstall" class="close-btn">
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </v-col>
                    </v-row>

                    <v-row class="mt-3">
                        <v-col cols="12">
                            <v-btn color="primary" block @click="installPWA" :loading="installingPWA">
                                <v-icon left>{{ isIOS ? 'mdi-apple' : 'mdi-download' }}</v-icon>
                                <span v-if="installingPWA">{{ $t('consultationPassenger.installing') }}</span>
                                <span v-else-if="isIOS">{{ isSafari ? $t('consultationPassenger.verInstructions') :
                                    $t('consultationPassenger.openSafari') }}</span>
                                <span v-else>{{ canInstallPWA ? $t('consultationPassenger.install') :
                                    $t('consultationPassenger.installManually') }}</span>
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-dialog>

        <div v-if="!showSplash" 
            :aria-hidden="anyDialogOpen ? 'true' : 'false'"
            :inert="anyDialogOpen ? true : null">
            <!-- Aviso de localização não disponível -->
            <v-alert v-if="!locationAvailable" type="error" prominent class="mb-4" style="margin: 16px;">
                <v-row align="center">
                    <v-col class="grow">
                        <div class="text-subtitle-1 font-weight-bold">
                            {{ $t('consultationPassenger.locationNotAvailable') }}
                        </div>
                        <div class="text-body-2">
                            {{ $t('consultationPassenger.description') }}
                            {{ $t('consultationPassenger.description2') }}
                        </div>
                    </v-col>
                    <v-col class="shrink">
                        <v-btn color="white" small fab @click="irParaTroubleshooting" aria-label="Ajuda para problemas de localização">
                            <v-icon color="red" aria-hidden="true">mdi-help</v-icon>
                        </v-btn>
                    </v-col>
                </v-row>
            </v-alert>

            <!-- ARIA live region for screen reader announcements -->
            <div aria-live="polite" aria-atomic="true" role="status" class="sr-only" id="proximity-announcer">
                {{ proximityAnnouncement }}
            </div>
            
            <!-- ARIA live region for map marker clicks and focus -->
            <div aria-live="assertive" aria-atomic="true" role="alert" class="sr-only" id="interaction-announcer">
                {{ markerClickAnnouncement }}
            </div>
            
            <!-- ARIA live region for dialog announcements -->
            <div aria-live="assertive" aria-atomic="true" role="alert" class="sr-only" id="dialog-announcer">
                {{ dialogAnnouncement }}
            </div>

            <main id="main-content" role="main" aria-label="Informações de ônibus próximos">
                <h1 class="sr-only">{{ $t('passenger_consult.labels.title') }}</h1>
                
                <v-container style="margin-top: 2%; width: 100vw; height: 100%; padding-bottom: 56px;">

                    <!-- View: Home -->
                    <div v-if="currentView === 'home'">
                <v-row justify="center" style="z-index: 500;">
                    <!-- Avisos -->
                    <AvisosBanner :avisos="avisos" :intervalo="5000" style="margin-right: 1%; margin-left: 1%;"/>
 
                    <v-dialog v-model="dialogoMapa" persistent fullscreen style="border-radius: 10px;"
                        role="dialog"
                        aria-labelledby="dialog-title-hidden">
                        <h2 id="dialog-title-hidden" class="sr-only">{{ dialogTitle }}</h2>
                        <v-card>
                            <v-toolbar color="primary" dark flat style="border-radius: 0; min-height: 56px;">
                                <v-toolbar-title aria-hidden="true"
                                    style="font-size: 1.2em; font-weight: bold; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                                    {{ dialogTitle }}
                                </v-toolbar-title>
                                <v-spacer></v-spacer>
                                <v-btn icon @click="fechaDialogo" aria-label="Fechar">
                                    <v-icon aria-hidden="true">mdi-close</v-icon>
                                </v-btn>
                            </v-toolbar>
                            <v-card-text style="padding: 0;" :aria-hidden="dialogJustOpened">
                                <!-- Sentido selector - accessible -->
                                <div style="position: absolute; top: 70px; right: 16px; z-index: 1000;">
                                    <v-card class="pa-1" style="min-width: 120px; max-width: 180px;">
                                        <v-card-text class="pa-1" style="padding: 4px !important;">
                                            <v-row justify="center" align="center" no-gutters>
                                                <v-col cols="6" class="pa-0 text-center"
                                                    @click="sentidoChanged()" @keydown.enter="sentidoChanged()" 
                                                    style="cursor:pointer;" tabindex="0"
                                                    role="button"
                                                    :aria-label="`Sentido Ida ${!idaDisabled ? 'selecionado' : ''}`">
                                                    <v-icon :color="idaDisabled ? 'grey' : 'green'" small
                                                        style="font-size: 20px;" aria-hidden="true">mdi-bus</v-icon>
                                                    <div aria-hidden="true"
                                                        style="font-size: 12px; font-weight: bold; line-height: 1;">
                                                        {{ $t('passenger_consult.labels.direction_oneway') }}</div>
                                                </v-col>
                                                <v-col cols="6" class="pa-0 text-center"
                                                    @click="sentidoChanged()" @keydown.enter="sentidoChanged()" 
                                                    style="cursor:pointer;" tabindex="0"
                                                    role="button"
                                                    :aria-label="`Sentido Volta ${!voltaDisabled ? 'selecionado' : ''}`">
                                                    <v-icon :color="voltaDisabled ? 'grey' : 'orange'" small
                                                        style="font-size: 20px;" aria-hidden="true">mdi-bus</v-icon>
                                                    <div aria-hidden="true"
                                                        style="font-size: 12px; font-weight: bold; line-height: 1;">
                                                        {{ $t('passenger_consult.labels.direction_roundtrip') }}</div>
                                                </v-col>
                                            </v-row>
                                        </v-card-text>
                                    </v-card>
                                </div>
                                
                                <div id="mapcontainer" ref="mapDiv" :key="key" aria-hidden="true" tabindex="-1">
                                    <!-- <v-snackbar v-model="loading" dark centered timeout="-1">
                                        Procurando Veículos...
                                        <v-progress-linear height="10" color="primary" indeterminate>
                                        </v-progress-linear>
                                    </v-snackbar> -->
                                    <l-map ref="map" :zoom="15" :center="center" :options="mapOptions"
                                        @ready="onMapResize" v-resize="onMapResize">
                                        <l-tile-layer :url="url" attribution="" />
                                        <l-marker :lat-lng="latLng(lat, lng)" :icon="getUserIcon()"
                                            :z-index-offset="1"
                                            @click="announceUserLocation"></l-marker>

                                        <l-marker v-for="(veiculo, v) in veiculosFiltered"
                                            :key="`VEICULO ${veiculo.id_veiculo}-${v}`"
                                            :lat-lng="latLng(veiculo.lat, veiculo.lng)" :icon="getIcon(veiculo)"
                                            :z-index-offset="1000"
                                            :title="`Veículo ${veiculo.cod_veiculo} - ${veiculo.gps_offline ? 'Offline' : 'Online'}`"
                                            :aria-label="`Veículo ${veiculo.cod_veiculo}, status: ${veiculo.gps_offline ? 'Offline' : 'Online'}`"
                                            @click="announceVehicleClick(veiculo)">
                                            <l-tooltip
                                                :options="{ permanent: true, direction: 'top', opacity: 1.0, className: 'veiculo__tooltip', offset: [0, -25] }">
                                                <div>
                                                    <span :class="veiculo.gps_offline ? 'text-red' : 'text-green'">
                                                        {{ veiculo.cod_veiculo }}
                                                    </span>
                                                    <br>
                                                    <span :class="veiculo.gps_offline ? 'text-red' : 'text-green'">
                                                        {{ veiculo.gps_offline ? 'Offline' : 'Online' }}
                                                    </span>
                                                </div>
                                            </l-tooltip>
                                        </l-marker>

                                        <!-- Separate timestamp badges that don't inherit blink animation -->
                                        <l-marker v-for="(veiculo, v) in veiculosFiltered"
                                            :key="`TIMESTAMP ${veiculo.id_veiculo}-${v}`"
                                            :lat-lng="latLng(veiculo.lat, veiculo.lng)"
                                            :icon="getTimestampIcon(veiculo)" :z-index-offset="1001"
                                            :title="`Última atualização do veículo ${veiculo.cod_veiculo}: ${veiculo.dt_gps}`"
                                            :aria-label="`Última atualização do veículo ${veiculo.cod_veiculo}: ${veiculo.dt_gps}`"
                                            @click="announceTimestampClick(veiculo)">
                                        </l-marker>

                                        <l-marker v-for="(parada, p) in paradasMapaFiltered"
                                            :key="`PARADA ${parada.id_parada}-${p}`"
                                            :lat-lng="latLng(parada.lat_centro, parada.lng_centro)"
                                            :icon="getIconParada()"
                                            @click="announceStopClick(parada)">
                                            <l-tooltip :key="`tooltip-parada-${(parada.id_cerca != null ? parada.id_cerca : parada.id_parada)}-${isParadaSelecionadaNoSinotico(parada)}}`"
                                                :content="parada.nome_cerca"
                                                :options="{ permanent: isParadaSelecionadaNoSinotico(parada), direction: 'top', opacity: 1.0, className: 'veiculo__tooltip', offset: [0, -10] }"></l-tooltip>
                                        </l-marker>

                                        <l-geo-json v-for="(rota, r) in rotasFiltered"
                                            :key="`ROTA ${rota.geojson.id}-${r}`" :geojson="rota.geojson"
                                            :options-style="getPolylineStyle">
                                        </l-geo-json>
                                    </l-map>
                                </div>
                                
                                <!-- Card de Parada Mais Próxima e Veículo -->
                                <v-card v-if="paradaSinoticoCard" 
                                    class="mb-3 parada-proxima-card" 
                                    elevation="3"
                                    :color="sentidoSinotico.id === 1 ? '#e8f5e9' : '#fff3e0'"
                                    @click="centralizarParadaExibida"
                                    style="cursor: pointer;">
                                    <v-card-text class="py-2 px-3">
                                        <v-row align="center" no-gutters>
                                            <v-col cols="auto" class="pr-3">
                                                <v-icon :color="sentidoSinotico.id === 1 ? '#008000' : '#FF5722'" size="32">
                                                    mdi-map-marker-distance
                                                </v-icon>
                                            </v-col>
                                            <v-col>
                                                <div class="text-caption grey--text text--darken-1">{{ isParadaSelecionadaMesmaQueProxima ? $t('consultationPassenger.closestStop') : ((paradaSelecionadaSinotico || (paradaSinoticoCard && paradaSinoticoCard.parada)) ? $t('consultationPassenger.selectedStop') : $t('consultationPassenger.closestStop')) }}</div>
                                                <div class="text-body-2 font-weight-bold" :style="{ color: sentidoSinotico.id === 1 ? '#008000' : '#FF5722' }">
                                                    {{ (paradaSinoticoCard && (paradaSinoticoCard.parada ? paradaSinoticoCard.parada.nome_cerca : paradaSinoticoCard.nome_cerca)) || '' }}
                                                </div>
                                                <div v-if="distanciaSinoticoCard" class="text-caption">
                                                    <v-icon small :color="sentidoSinotico.id === 1 ? '#008000' : '#FF5722'">mdi-walk</v-icon>
                                                    {{ distanciaSinoticoCard }}
                                                </div>
                                            </v-col>
                                        </v-row>
                                        
                                        <!-- Informação do Veículo Mais Próximo -->
                                        <v-divider v-if="getTempoChegadaParadasSinoticoCard" class="my-2"></v-divider>
                                        <v-row v-if="getTempoChegadaParadasSinoticoCard" align="center" no-gutters class="mt-1">
                                            <v-col cols="auto" class="pr-3">
                                                <v-icon :color="sentidoSinotico.id === 1 ? '#008000' : '#FF5722'" size="32">
                                                    mdi-bus-clock
                                                </v-icon>
                                            </v-col>
                                            <v-col>
                                                <div class="text-caption grey--text text--darken-1" align="">{{  getTempoChegadaParadasSinoticoCard.veiculo.cod_veiculo }}</div>
                                                <div class="text-caption">
                                                    <v-icon small :color="sentidoSinotico.id === 1 ? '#008000' : '#FF5722'">mdi-clock-outline</v-icon>
                                                    <span>
                                                      {{ getTempoChegadaParadasSinoticoCard.tempoEstimado.texto }}
                                                    </span>
                                                </div>
                                            </v-col>
                                        </v-row>
                                        
                                        <!-- Mensagem quando há veículo em rota com parada anterior à selecionada (ETA ainda não disponível) -->
                                        <v-row v-if="!getTempoChegadaParadasSinoticoCard && temVeiculoEmRotaComCercaAnterior" align="center" no-gutters class="mt-2">
                                            <v-col>
                                                <div class="text-caption grey--text text--darken-2 text-center">
                                                    <v-icon small color="grey darken-1">mdi-information-outline</v-icon>
                                                    {{ $t('consultationPassenger.calculatingETA') }}
                                                </div>
                                            </v-col>
                                        </v-row>
                                        <!-- Mensagem quando não há veículos na rota ou nenhum com ETA para essa parada na linha selecionada -->
                                        <v-row v-if="!getTempoChegadaParadasSinoticoCard && !temVeiculoEmRotaComCercaAnterior" align="center" no-gutters class="mt-2">
                                            <v-col>
                                                <div class="text-caption grey--text text--darken-2 text-center">
                                                    <v-icon small color="grey darken-1">mdi-information-outline</v-icon>
                                                    {{ $t('consultationPassenger.noVehicleAvailable') }}
                                                </div>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>

                                <SinoticoParadas ref="sinotico" :key="keySinotico" :titulo="sentidoSinotico.descricao"
                                    :veiculos="getVeiculosSinotico()"
                                    :paradas="getParadasSinotico()"
                                    :vertical="vertical" :pontoInicial="pontoInicial" :pontoFinal="pontoFinal"
                                    :corDestaque="sentidoSinotico.id === 1 ? '#008000' : '#FF5722'"
                                    :sentido-id="sentidoSinotico.id"
                                    :pontoSelecionado="paradaParaPontoSelecionadoSinotico"
                                    :pontoMaisProximo="paradaMaisProximaSinotico ? paradaMaisProximaSinotico.parada : null"
                                    @parada-selected="onParadaSelecionada"
                                    @ponto-focus="announceSinoticoFocus" />
                            </v-card-text>
                        </v-card>
                    </v-dialog>
                </v-row>

                <div class="ponto-proximo">
                    <svg width="32px" height="32px" viewBox="0 0 24 24"
                        xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#" xmlns="http://www.w3.org/2000/svg"
                        version="1.1" xmlns:cc="http://creativecommons.org/ns#"
                        xmlns:dc="http://purl.org/dc/elements/1.1/" style="min-width: 32px; min-height: 32px;">
                        <g transform="translate(0 -1028.4)">
                            <path
                                d="m12 0c-4.4183 2.3685e-15 -8 3.5817-8 8 0 1.421 0.3816 2.75 1.0312 3.906 0.1079 0.192 0.221 0.381 0.3438 0.563l6.625 11.531 6.625-11.531c0.102-0.151 0.19-0.311 0.281-0.469l0.063-0.094c0.649-1.156 1.031-2.485 1.031-3.906 0-4.4183-3.582-8-8-8zm0 4c2.209 0 4 1.7909 4 4 0 2.209-1.791 4-4 4-2.2091 0-4-1.791-4-4 0-2.2091 1.7909-4 4-4z"
                                transform="translate(0 1028.4)" fill="#e74c3c" />
                            <path
                                d="m12 3c-2.7614 0-5 2.2386-5 5 0 2.761 2.2386 5 5 5 2.761 0 5-2.239 5-5 0-2.7614-2.239-5-5-5zm0 2c1.657 0 3 1.3431 3 3s-1.343 3-3 3-3-1.3431-3-3 1.343-3 3-3z"
                                transform="translate(0 1028.4)" fill="#c0392b" />
                        </g>
                    </svg>
                    <v-container>
                        <v-col cols="12" sm="10" md="10">
                            <v-row>
                                <span style="font-size: small;">{{ $t('consultationPassenger.nearestPoint') }}</span>
                            </v-row>
                            <v-row>
                                <v-autocomplete 
                                    label="Parada" 
                                    v-model="paradaSelecionadaPainel" 
                                    :items="paradasProximas"
                                    item-text="nome_cerca" 
                                    item-value="id_cerca" 
                                    return-object 
                                    dense 
                                    outlined
                                    hide-details 
                                    no-data-text="Nenhuma parada encontrada"
                                    @change="paradaChanged = true; announceStopChange();"
                                    @focus="announceStopSelectFocus"
                                    tabindex="0"
                                    aria-label="Selecionar parada"
                                    aria-live="polite"
                                    style="margin-top: 5px; font-weight: bold; max-width: 320px; width: auto; min-width: 30px;">
                                </v-autocomplete>
                            </v-row>
                            <v-row>
                                <span style="font-size: small;">{{ enderecoCerca }}</span>
                            </v-row>
                        </v-col>
                    </v-container>
                </div>
                <v-row v-if="ultimaAtualizacaoEta" dense class="mb-1">
                    <v-col cols="12" class="py-0">
                        <div class="text-caption text-start" style="color: black;">
                            <v-icon small style="vertical-align: middle;">mdi-update</v-icon>
                            Última atualização: {{ ultimaAtualizacaoEta }}
                        </div>
                    </v-col>
                </v-row>
                <v-row dense>
                    <template v-if="!locationAvailable && linhasFiltered.length === 0">
                        <v-col cols="12" align-self="center">
                            <v-card class="pa-4 text-center">
                                <v-icon large color="grey" class="mb-2">mdi-map-marker-off</v-icon>
                                <div class="text-h6 mb-2">{{ $t('consultationPassenger.locationRequired') }}</div>
                                <div class="text-body-2 mb-3">
                                    {{ $t('consultationPassenger.locationRequiredDescription') }}
                                </div>
                                <v-btn color="primary" icon @click="requestLocation" :loading="requestingLocation">
                                    <v-icon>mdi-reload</v-icon>
                                </v-btn>
                            </v-card>
                        </v-col>
                    </template>
                    <template v-else-if="linhasFiltered.length === 0">
                        <v-col cols="12" align-self="center">
                            <v-card class="pa-4 text-center">
                                <v-icon large color="grey" class="mb-2">mdi-bus-off</v-icon>
                                <div class="text-h6 mb-2">{{ $t('consultationPassenger.noVehiclesNearby') }}</div>
                                <div class="text-body-2">
                                    {{ $t('consultationPassenger.noVehiclesNearbyDescription') }}
                                </div>
                            </v-card>
                        </v-col>
                    </template>
                    <template v-else>
                        <v-col v-for="(veiculo, v) in linhasFiltered" :key="`LINHA ${veiculo.id_linha}-${v}`" cols="12"
                            align-self="center">
                            <div class="linha-card" :style="{ backgroundColor: getCardColor(veiculo) + '90 ' }"
                                @click="selecionarLinha(veiculo)"
                                @keydown.enter.prevent="selecionarLinha(veiculo)"
                                @focus="announceVehicleCardFocus(veiculo)" tabindex="0" role="button"
                                :aria-label="`${getNomeLinha(veiculo.id_linha)} - ${getSentido(veiculo.sentido)}, Veículo ${veiculo.cod_veiculo}, ${getTempoChegadaParadasPainelCard(veiculo, paradaSelecionadaPainel)}`">
                                <v-row>
                                    <v-col cols="4" align-self="center" style="font-size: small; font-weight: bold;">
                                        {{ getNomeLinha(veiculo.id_linha) }} - {{ getSentido(veiculo.sentido) }}
                                    </v-col>
                                    <v-col cols="4" align-self="center">
                                        <div style="display: flex; align-items: center; gap: 5px;">
                                            <v-icon small color="black">mdi-bus</v-icon>
                                            <span style="font-size: medium;">{{ veiculo.cod_veiculo }}</span>
                                        </div>
                                    </v-col>
                                    <v-col cols="4" align-self="center">
                                        <div style="display: flex; align-items: center; gap: 5px;">
                                            <v-icon
                                                small
                                                :color="veiculo.has_eta == 1 ? 'primary' : 'black'"
                                                :class="['tempo-icon', { 'tempo-icon--live': veiculo.has_eta == 1 }]"
                                            >
                                                {{ getTempoIcon(veiculo) }}
                                            </v-icon>
                                            <span :class="[getTempoChegadaClass(veiculo), { 'tempo-chegada--no-ponto': getTempoChegadaParadasPainelCard(veiculo, paradaSelecionadaPainel) === 'No ponto' }]">{{getTempoChegadaParadasPainelCard(veiculo, paradaSelecionadaPainel)}}</span>
                                        </div>
                                    </v-col>
                                </v-row>
                            </div>
                        </v-col>
                    </template>
                </v-row>

                <v-row justify="center">
                    <v-dialog v-model="linhasDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn color="primary" dark v-bind="attrs" v-on="on" width="93vw">
                                {{ $t('consultationPassenger.allLines') }}
                            </v-btn>
                        </template>
                        <v-card>
                            <v-toolbar dark color="primary">
                                <v-toolbar-title>{{ $t('consultationPassenger.lines') }}</v-toolbar-title>
                                <v-spacer></v-spacer>
                                <v-btn icon dark @click="linhasDialog = false" title="Fechar" aria-label="Fechar lista de linhas">
                                    <v-icon aria-hidden="true">mdi-close</v-icon>
                                </v-btn>
                            </v-toolbar>
                            <v-list role="list" aria-label="Lista de linhas de ônibus">
                                <v-list-item v-for="(linha, index) in linhas" :key="index"
                                    @click="selecionarLinhaDoDialog(linha)"
                                    role="listitem"
                                    :aria-label="`Linha ${linha.nome_linha}`"
                                    tabindex="0"
                                    @keydown.enter.prevent="selecionarLinhaDoDialog(linha)">
                                    <v-list-item-content aria-hidden="true">
                                        <v-list-item-title>{{ linha.nome_linha }}</v-list-item-title>
                                        <v-list-item-subtitle>{{ linha.cod_linha }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                </v-list-item>
                            </v-list>
                        </v-card>
                    </v-dialog>
                </v-row>
                    </div>
                    <!-- Fim View: Home -->

                    <!-- View: Viagens Previstas -->
                    <div v-if="currentView === 'viagensPrevistas'">
                        <v-row class="mt-0 ma-0">
                            <v-col cols="12" class="pa-0">
                                <v-card class="rounded-0">
                                    <v-card-title class="primary white--text">
                                        <v-icon left color="white">mdi-bus-clock</v-icon>
                                        {{ $t('consultationPassenger.predictedTrips') || 'Viagens Previstas' }}
                                    </v-card-title>
                                    <v-card-text class="pa-4">
                                        <v-select
                                            v-model="linhaSelectedFilter"
                                            :items="linhas"
                                            item-text="nome_linha"
                                            item-value="nome_linha"
                                            label="Linha"
                                            prepend-inner-icon="mdi-bus"
                                            outlined
                                            dense
                                            clearable
                                            hide-details
                                            class="mb-4"
                                        ></v-select>
                                        
                                        <v-data-table
                                            :headers="viagensHeaders"
                                            :items="viagensPrevistas"
                                            :no-data-text="$t('consultationPassenger.noTripsScheduled') || 'Nenhuma viagem prevista'"
                                            class="elevation-1"
                                            disable-sort
                                            style="font-size: small;"
                                        >
                                        </v-data-table>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>
                    </div>
                    <!-- Fim View: Viagens Previstas -->
            </v-container>

            <!-- Bottom Navigation -->
            <v-bottom-navigation
                v-model="currentView"
                fixed
                color="primary"
                grow
                app
            >
                <v-btn value="home" @click="navigateToHome">
                    <span>{{ $t('consultationPassenger.homePage') || 'Início' }}</span>
                    <v-icon>mdi-home</v-icon>
                </v-btn>

                <v-btn value="viagensPrevistas" @click="navigateToViagensPrevistas">
                    <span>{{ $t('consultationPassenger.predictedTrips') || 'Viagens' }}</span>
                    <v-icon>mdi-bus-clock</v-icon>
                </v-btn>
            </v-bottom-navigation>
            </main>
        </div>
    </div>
</template>

<script>
import apiController from "@/utils/apiController";
import { getIdModo } from "@/constants";
import SplashScreen from './SplashScreen.vue';
import AvisosBanner from './AvisosBanner.vue';
import { LMap, LTileLayer, LTooltip, LMarker, LGeoJson } from "vue2-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import SinoticoParadas from './SinoticoParadas.vue';
import EventBus from '@/utils/eventBus';
import { haversineMeters } from '@/utils/haversine';
var GeoLib = require('geolib');

export default {
    components: {
        LMap,
        LTileLayer,
        LTooltip,
        LMarker,
        LGeoJson,
        SplashScreen,
        SinoticoParadas,
        AvisosBanner
    },
    data: () => ({
        url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        attribution:
            '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
        center: [-22.88746, -43.29185],
        mapOptions: {
            zoomSnap: 0.5,
            attributionControl: false,
        },
        user: null,
        token: null,
        loading: false,
        loadingLinhas: false,
        veiculos: [],
        veiculosAtivos: [],
        linhas: [],
        idLinhaSelected: null,
        recursos: [],
        timeout: null,
        numeroDeCards: 10,
        lat: null,
        lng: null,
        raio: 1000,
        idaDisabled: false,
        voltaDisabled: false,
        loadingRotas: false,
        rotas: [],
        paradas: [],
        /** Paradas por id_linha para filtro do painel sem depender de clique no card. */
        paradasPorLinha: {},
        paradasProximas: [],
        /** Lista completa de getcercamaisproxima (uma entrada por id_cerca+linha+sentido). */
        paradasProximasCompletas: [],
        paradaSelecionadaPainel: null,
        /** Veículo cujo card foi clicado no painel; usado no sinótico para exibir cod_veiculo e ETA desse veículo em vez do menor ETA. */
        veiculoSelecionadoPainel: null,
        paradaChanged: false,
        pendingParadaSelecionadaId: null,
        viagens: [],
        userMoved: false,
        dialogoMapa: false,
        dialogJustOpened: false,
        dialogAnnouncement: '',
        key: 0,
        linhasDialog: false,
        showSplash: true,
        splashMinTimePassed: false,
        splashDataLoaded: false,
        locationAvailable: false,
        requestingLocation: false,
        keySinotico: 'sin-1',
        showPWAInstall: false,
        installingPWA: false,
        canInstallPWA: false,
        deferredPrompt: null,
        isIOS: false,
        isStandalone: false,
        isSafari: false,
        fixLocation: false,
        markerClickAnnouncement: '',
        avisos: [],
        acessoRegistrado: false,
        idAcesso: null,
        currentView: 'home', // 'home' ou 'viagensPrevistas'
        viagensPrevistas: [],
        linhaSelectedFilter: null, // Filtro de linha selecionada para viagens previstas
        viagensHeaders: [
            { text: 'Sentido', value: 'sentido', align: 'center' },
            { text: 'Saída', value: 'hora_prevista_saida', align: 'center' },
            { text: 'Chegada', value: 'hora_prevista_chegada', align: 'center' }],
        paradaSelecionadaSinotico: null, // Parada clicada no sinótico
        /** Quando true (abertura pelo dialog de linhas), o card do sinótico prioriza paradaMaisProximaSinotico sobre paradaSelecionadaPainel */
        selectParadaMaisProximaNoSinotico: false,
        etaParadasCache: {}, // Cache de ETAs por linha/sentido: { 'linha_sentido': { id_veiculo_id_cerca: eta_segundos } }
    }),
    computed: {
        anyDialogOpen() {
            // Check if any dialog is currently open
            return this.dialogoMapa || this.linhasDialog || this.showPWAInstall;
        },
        dialogTitle() {
            if (this.idLinhaSelected) {
                return this.getNomeLinha(this.idLinhaSelected);
            }
            return this.$tc('line.self', 1);
        },
        veiculosFiltered() {
            if (!this.idLinhaSelected) return [];

            let result = this.veiculosAtivos.filter(v => 
                v.sem_comunicacao == 0 && 
                v.id_linha == this.idLinhaSelected &&
                v.id_viagem != null
            );

            if (this.idaDisabled) {
                result = result.filter((v) => v.sentido != 0);
            }

            if (this.voltaDisabled) {
                result = result.filter((v) => v.sentido != 1);
            }

            // Remove duplicatas por id_veiculo
            const veiculosUnicos = new Map();
            result.forEach(v => {
                if (!veiculosUnicos.has(v.id_veiculo)) {
                    veiculosUnicos.set(v.id_veiculo, v);
                }
            });

            return Array.from(veiculosUnicos.values());
        },
        rotasFiltered() {
            if (!this.idLinhaSelected) {
                return [];
            }

            let result = this.rotas.filter((r) => r.id_linha == this.idLinhaSelected);

            if (this.idaDisabled) {
                result = result.filter((r) => r.id_sentido == 2);
            }

            if (this.voltaDisabled) {
                result = result.filter((r) => r.id_sentido == 1);
            }

            return result;
        },
        paradasFiltered() {
            if (!this.idLinhaSelected) {
                return [];
            }
            // Filter for selected line and exclude tipo_cerca 1 (Via) and 4 (possibly inactive/control)
            let result = this.paradas.filter((r) => 
                r.id_linha == this.idLinhaSelected &&
                r.id_tipo_cerca != 1 && 
                r.id_tipo_cerca != 4
            );
            
            if (this.idaDisabled) {
                result = result.filter((r) => r.id_sentido == 2 || r.id_sentido == -1);
            }
            if (this.voltaDisabled) {
                result = result.filter((r) => r.id_sentido == 1 || r.id_sentido == -1);
            }
            return result;
        },
        /** Mesmas cercas exibidas no sinótico: terminais do sentido + paradas intermediárias (tipo 5). */
        paradasMapaFiltered() {
            if (!this.idLinhaSelected) return [];

            const seenIds = new Set();
            const result = [];

            const add = (parada) => {
                if (!parada) return;
                const id = parada.id_cerca;
                if (id == null || seenIds.has(id)) return;
                seenIds.add(id);
                result.push(parada);
            };

            add(this.pontoInicial);
            this.getParadasSinotico().forEach(add);
            add(this.pontoFinal);

            return result;
        },
        /** Veículos do painel: getveiculospainel (linha passa na parada), com ou sem viagem aberta; enriquecidos por veiculosAtivos quando houver match. */
        veiculosPainelEmLinha() {
            if (!this.veiculos || !this.veiculos.length) return [];
            const ativoByKey = new Map(
                (this.veiculosAtivos || []).map((a) => [`${a.id_veiculo}-${a.id_linha}`, a])
            );
            const seen = new Set();
            const result = [];
            for (const v of this.veiculos) {
                const key = `${v.id_veiculo}-${v.id_linha}`;
                if (seen.has(key)) continue;
                seen.add(key);
                const ativo = ativoByKey.get(key);
                result.push({
                    ...v,
                    id_viagem: ativo?.id_viagem ?? v.id_viagem ?? null,
                    sem_comunicacao: ativo?.sem_comunicacao ?? 0,
                    has_eta: ativo?.has_eta ?? v.has_eta,
                    eta_segundos_parada_painel: v.eta_segundos_proxima_parada,
                    eta_segundos_proxima_parada: ativo?.eta_segundos_proxima_parada ?? v.eta_segundos_proxima_parada,
                    eta_segundos_parada_final: ativo?.eta_segundos_parada_final ?? v.eta_segundos_parada_final,
                    dt_atualizacao_eta: ativo?.dt_atualizacao_eta ?? v.dt_atualizacao_eta,
                    ordem_parada_atual: ativo?.ordem_parada_atual,
                    ordem_parada_efetiva: ativo?.ordem_parada_efetiva,
                    ordem_parada_ultima: ativo?.ordem_parada_ultima,
                    ordem_cerca_proxima_parada: ativo?.ordem_cerca_proxima_parada,
                    id_cerca_ultima_parada: ativo?.id_cerca_ultima_parada,
                    id_cerca: ativo?.id_cerca ?? v.id_cerca,
                    id_cerca_proxima_parada: ativo?.id_cerca_proxima_parada
                });
            }
            return result;
        },
        linhasFiltered() {
            let lista;
            if (getIdModo() === 2) {
                lista = this.veiculosPainelEmLinha.slice(0, this.numeroDeCards);
            } else if (this.paradaSelecionadaPainel) {
                lista = this.veiculosPainelEmLinha;
            } else {
                return [];
            }
            lista = lista.filter((v) => this.veiculoVisivelNoPainel(v));
            // Ordenar: ETA válido primeiro; depois ordem na rota; agendados sem viagem aberta por último
            return lista.slice().sort((a, b) => {
                const etaA = this.getETASegundosParaParadaPainel(a);
                const etaB = this.getETASegundosParaParadaPainel(b);
                const numA = etaA != null && Number.isFinite(etaA) ? etaA : Infinity;
                const numB = etaB != null && Number.isFinite(etaB) ? etaB : Infinity;
                if (numA !== numB) return numA - numB;
                if (a.id_linha !== b.id_linha) return (a.id_linha || 0) - (b.id_linha || 0);
                const ordemA = this.getOrdemVeiculoEfetiva(a);
                const ordemB = this.getOrdemVeiculoEfetiva(b);
                const ordemNumA = ordemA != null ? ordemA : Infinity;
                const ordemNumB = ordemB != null ? ordemB : Infinity;
                if (ordemNumA !== ordemNumB) return ordemNumA - ordemNumB;
                const viagemA = a.id_viagem != null ? 0 : 1;
                const viagemB = b.id_viagem != null ? 0 : 1;
                return viagemA - viagemB;
            });
        },
        ultimaAtualizacaoEta() {
            if (!this.veiculosPainelEmLinha || !this.veiculosPainelEmLinha.length) return null;
            const datas = this.veiculosPainelEmLinha
                .map((v) => v.dt_atualizacao_eta)
                .filter((d) => d != null && d !== '');
            if (!datas.length) return null;
            const maisRecente = new Date(Math.max(...datas.map((d) => new Date(d).getTime())));
            if (isNaN(maisRecente.getTime())) return null;
            return maisRecente.toLocaleString(undefined, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
        },
        enderecoCerca() {
            if (this.paradaSelecionadaPainel) {
                if (!this.paradaSelecionadaPainel.endereco || this.paradaSelecionadaPainel.endereco == 'undefined') {
                    return this.$t('consultationPassenger.enderecoNotAvailable');
                }
                return this.paradaSelecionadaPainel.endereco;
            }

            return '';
        },
        paradaMaisProximaSinotico() {
            // Calcula a parada mais próxima da linha aberta no diálogo/sinótico
            if (!this.lat || !this.lng || !this.idLinhaSelected) {
                return null;
            }

            // Filtrar paradas da linha selecionada e do sentido (inclui paradas finais id_sentido = -1)
            const paradasDaLinha = this.paradas.filter(p => 
                p.id_linha === this.idLinhaSelected && 
                (p.id_sentido == this.sentidoSinotico.id || p.id_sentido == -1)
            );
            
            if (paradasDaLinha.length === 0) {
                return null;
            }

            let paradaMaisProxima = null;
            let menorDistancia = Infinity;

            for (const parada of paradasDaLinha) {
                if (parada.lat_centro && parada.lng_centro) {
                    const distancia = haversineMeters(
                        parseFloat(this.lat),
                        parseFloat(this.lng),
                        parseFloat(parada.lat_centro),
                        parseFloat(parada.lng_centro)
                    );

                    if (distancia < menorDistancia) {
                        menorDistancia = distancia;
                        paradaMaisProxima = parada;
                    }
                }
            }

            if (!paradaMaisProxima) {
                return null;
            }

            return {
                parada: paradaMaisProxima,
                distanciaMetros: Math.round(menorDistancia),
                distanciaKm: (menorDistancia / 1000).toFixed(2),
                distanciaTexto: menorDistancia < 1000 
                    ? `${Math.round(menorDistancia)} m` 
                    : `${(menorDistancia / 1000).toFixed(2)} km`
            };
        },

        paradaSinoticoCard() {
            // Parada selecionada no sinótico, parada do painel (incl. ponto final) na linha atual, ou parada mais próxima
            const paradaPainelNaLinha = this.paradaSelecionadaPainel && this.idLinhaSelected && this.paradaSelecionadaPainel.id_linha === this.idLinhaSelected
                ? { parada: this.paradaSelecionadaPainel }
                : null;

            // Ao abrir pelo dialog de linhas, priorizar parada mais próxima do usuário (não a do painel)
            if (this.selectParadaMaisProximaNoSinotico) {
                return this.paradaMaisProximaSinotico?.parada || null;
            }

            return this.paradaSelecionadaSinotico || paradaPainelNaLinha || this.paradaMaisProximaSinotico;
        },
        distanciaSinoticoCard() {
            // Distância até a parada exibida no card (incl. ponto final do painel)
            if (!this.lat || !this.lng) return null;
            const parada = (this.paradaSinoticoCard && (this.paradaSinoticoCard.parada || this.paradaSinoticoCard)) || null;
            // Aceitar lat/lng (painel - getcercamaisproxima) ou lat_centro/lng_centro (sinótico - getcercasporlinha)
            const latParada = parada && (parada.lat_centro != null ? parada.lat_centro : parada.lat);
            const lngParada = parada && (parada.lng_centro != null ? parada.lng_centro : parada.lng);
            if (!parada || latParada == null || lngParada == null) return null;
            
            const distancia = haversineMeters(
                parseFloat(this.lat),
                parseFloat(this.lng),
                parseFloat(latParada),
                parseFloat(lngParada)
            );
            
            return distancia < 1000 
                ? `${Math.round(distancia)} m` 
                : `${(distancia / 1000).toFixed(2)} km`;
        },
        isParadaSelecionadaMesmaQueProxima() {
            // Compara se a parada selecionada é a mesma que a mais próxima (por nome)
            if (!this.paradaSelecionadaSinotico || !this.paradaMaisProximaSinotico || !this.paradaMaisProximaSinotico.parada) {
                return false;
            }
            return this.paradaSelecionadaSinotico.nome_cerca === this.paradaMaisProximaSinotico.parada.nome_cerca;
        },
        /** Parada a destacar no sinótico: ao abrir por linha, usa a parada mais próxima do usuário (pulso + nome no mapa); senão a clicada ou a do painel. */
        paradaParaPontoSelecionadoSinotico() {
            return this.paradaSelecionadaSinotico || (this.paradaMaisProximaSinotico && this.paradaMaisProximaSinotico.parada) || this.paradaSelecionadaPainel || null;
        },
        getTempoChegadaParadasSinoticoCard() {
            // Calcula veículo mais próximo da parada exibida no card (incl. ponto final). Se um card foi selecionado no painel, usa esse veículo (cod e ETA); senão usa o menor ETA.
            if (!this.paradaSinoticoCard || !this.idLinhaSelected) return null;

            const paradaExibida = this.paradaParaPontoSelecionadoSinotico;
            if (!paradaExibida) return null;

            // Para terminais (ponto final): cache ETA usa id_sentido = -1
            const isPontoInicialDoSentido = this.pontoInicial && paradaExibida && paradaExibida.id_cerca === this.pontoInicial.id_cerca;
            const isPontoFinalDoSentido = this.pontoFinal && paradaExibida && paradaExibida.id_cerca === this.pontoFinal.id_cerca;
            const isTerminal = (p) => p && (p.id_tipo_cerca === 2 && this.sentidoSinotico.id == 2 || p.id_tipo_cerca === 3 && this.sentidoSinotico.id == 1);
            const idSentidoETA = isTerminal(paradaExibida) ? -1 : (paradaExibida.id_sentido != null ? paradaExibida.id_sentido : this.sentidoSinotico.id);

            // Parada é o ponto inicial: só exibir veículo se estiver na cerca (textoETA "No ponto"); senão null (não há veículo com menor ETA).
            if (isPontoInicialDoSentido) {
                const veiculosEmViagemDaLinha = this.veiculosAtivos.filter(v =>
                    v.id_linha === this.idLinhaSelected &&
                    v.sentido + 1 === this.sentidoSinotico.id &&
                    v.sem_comunicacao === 0 &&
                    v.id_viagem != null
                );
                const veiculoNoPontoInicial = veiculosEmViagemDaLinha.find(v =>
                    v.id_cerca != null && Number(v.id_cerca) === Number(paradaExibida.id_cerca)
                );
                if (veiculoNoPontoInicial) {
                    return {
                        veiculo: veiculoNoPontoInicial,
                        tempoEstimado: { minutos: 0, texto: this.formatETAPorSegundos(0, true) },
                        parada: paradaExibida
                    };
                }
                return null;
            }

            const veiculosDaLinha = this.veiculosAtivos.filter(v =>
                v.id_linha === this.idLinhaSelected &&
                v.sentido + 1 === this.sentidoSinotico.id &&
                v.sem_comunicacao === 0 &&
                v.id_viagem != null &&
                v.lat != null && v.lng != null &&
                !isNaN(Number(v.lat)) && !isNaN(Number(v.lng))
            );

            if (veiculosDaLinha.length === 0) return null;

            // Se há veículo selecionado no painel (mesma linha e sentido), usar esse veículo e seu ETA para a parada exibida
            const sel = this.veiculoSelecionadoPainel;
            if (sel && sel.id_linha == this.idLinhaSelected && sel.sentido === this.sentidoSinotico.id) {
                const veiculoSel = veiculosDaLinha.find(v => v.id_veiculo === sel.id_veiculo);
                if (veiculoSel) {
                    const paradaRef = this.getParadaRefParaVeiculo(veiculoSel, paradaExibida);
                    const passou = !isPontoFinalDoSentido && !isPontoInicialDoSentido && this.veiculoPassouParada(veiculoSel, paradaRef);
                    if (!passou) {
                        let etaSegundos;
                        if (isPontoFinalDoSentido) {
                            etaSegundos = veiculoSel.eta_segundos_parada_final != null ? Number(veiculoSel.eta_segundos_parada_final) : this.getETAVeiculoParada(veiculoSel.id_veiculo, paradaExibida.id_cerca, this.idLinhaSelected, idSentidoETA, veiculoSel) ?? this.getETAFromCacheOnly(veiculoSel.id_veiculo, paradaExibida.id_cerca, this.idLinhaSelected, idSentidoETA);
                        } else {
                            etaSegundos = this.getETAVeiculoParada(veiculoSel.id_veiculo, paradaExibida.id_cerca, this.idLinhaSelected, idSentidoETA, veiculoSel) ?? this.getETAFromCacheOnly(veiculoSel.id_veiculo, paradaExibida.id_cerca, this.idLinhaSelected, idSentidoETA);
                        }
                        const etaNum = Number(etaSegundos);
                        if (Number.isFinite(etaNum) && etaNum >= 0) {
                            const etaParaExibicao = veiculoSel.dt_atualizacao_eta ? this.AjusteETA(veiculoSel, etaNum) : etaNum;
                            const textoETA = this.formatETAPorSegundos(etaParaExibicao >= 0 ? etaParaExibicao : etaNum, veiculoSel.id_cerca === paradaExibida.id_cerca);
                            return {
                                veiculo: veiculoSel,
                                tempoEstimado: { minutos: Math.floor(etaNum / 60), texto: textoETA },
                                parada: paradaExibida
                            };
                        }
                    }
                }
            }

            let veiculoComMenorETA = null;
            let menorETA = Infinity;

            for (const veiculo of veiculosDaLinha) {
                const paradaRef = this.getParadaRefParaVeiculo(veiculo, paradaExibida);
                if (!isPontoFinalDoSentido && !isPontoInicialDoSentido && this.veiculoPassouParada(veiculo, paradaRef)) continue;

                // ETA: ponto final usa eta_segundos_parada_final; demais paradas usam getETAVeiculoParada e, se null, cache (para incluir veículos com ETA só no cache)
                let etaSegundos;
                if (isPontoFinalDoSentido) {
                    etaSegundos = veiculo.eta_segundos_parada_final != null ? Number(veiculo.eta_segundos_parada_final) : this.getETAVeiculoParada(veiculo.id_veiculo, paradaExibida.id_cerca, this.idLinhaSelected, idSentidoETA, veiculo) ?? this.getETAFromCacheOnly(veiculo.id_veiculo, paradaExibida.id_cerca, this.idLinhaSelected, idSentidoETA);
                } else {
                    etaSegundos = this.getETAVeiculoParada(veiculo.id_veiculo, paradaExibida.id_cerca, this.idLinhaSelected, idSentidoETA, veiculo) ?? this.getETAFromCacheOnly(veiculo.id_veiculo, paradaExibida.id_cerca, this.idLinhaSelected, idSentidoETA);
                }
                const etaNum = Number(etaSegundos);
                if (!Number.isFinite(etaNum) || etaNum < 0) continue;

                if (etaNum < menorETA) {
                    menorETA = etaNum;
                    veiculoComMenorETA = veiculo;
                }
            }

            // Se não encontrou veículo com ETA, retornar null
            if (!veiculoComMenorETA) return null;

            // Calcular minutos a partir do ETA em segundos
            const tempoMinutos = Math.floor(menorETA / 60);
            // Ajuste temporal só quando há dt_atualizacao_eta; senão usa ETA bruto (ex.: veículo com ETA só no cache)
            const etaParaExibicao = veiculoComMenorETA.dt_atualizacao_eta ? this.AjusteETA(veiculoComMenorETA, menorETA) : menorETA;
            const textoETA = this.formatETAPorSegundos(etaParaExibicao >= 0 ? etaParaExibicao : menorETA, veiculoComMenorETA.id_cerca === paradaExibida.id_cerca);

            return {
                veiculo: veiculoComMenorETA,
                tempoEstimado: {
                    minutos: tempoMinutos,
                    texto: textoETA
                },
                parada: paradaExibida
            };
        },
        /** True quando não há ETA mas existe pelo menos um veículo em rota cuja parada atual é anterior à parada selecionada no sinótico (veículo a caminho da parada). */
        temVeiculoEmRotaComCercaAnterior() {
            if (this.getTempoChegadaParadasSinoticoCard || !this.paradaSinoticoCard || !this.idLinhaSelected) return false;
            const paradaExibida = this.paradaParaPontoSelecionadoSinotico;
            if (!paradaExibida) return false;
            const isPontoFinalDoSentido = this.pontoFinal && paradaExibida && paradaExibida.id_cerca === this.pontoFinal.id_cerca;
            const ordemParada = paradaExibida.ordem_parada != null ? Number(paradaExibida.ordem_parada) : null;
            const veiculosEmRota = this.veiculosAtivos.filter(v =>
                v.id_linha === this.idLinhaSelected &&
                v.sentido + 1 === this.sentidoSinotico.id &&
                v.sem_comunicacao === 0 &&
                v.id_viagem != null
            );
            return veiculosEmRota.some(v => {
                const ordemVeiculo = this.getOrdemVeiculoEfetiva(v);
                if (isPontoFinalDoSentido) return true;
                if (ordemParada == null) return false;
                if (ordemVeiculo != null) return ordemVeiculo <= ordemParada;
                if (v.ordem_cerca_proxima_parada != null) return Number(v.ordem_cerca_proxima_parada) <= ordemParada;
                return v.id_viagem != null;
            });
        },
        sentidoSinotico() {
            if (this.idaDisabled) {
                return { id: 2, descricao: this.$t('operation.back') };
            }

            return { id: 1, descricao: this.$t('operation.go') };
        },
        vertical() {
            return window.innerWidth < 600;
        },
        pontoInicial() {
            const tipo = this.sentidoSinotico.id == 1 ? 2 : 3;
            return this.paradasFiltered.find(parada => parada.id_tipo_cerca === tipo);
        },
        pontoFinal() {
            const tipo = this.sentidoSinotico.id == 1 ? 3 : 2;
            return this.paradasFiltered.find(parada => parada.id_tipo_cerca === tipo);
        },
        proximityAnnouncement() {
            // Check if user location is available
            if (!this.lat || !this.lng || !this.veiculosPainelEmLinha.length) {
                return '';
            }

            // Distance threshold: 100 meters
            const PROXIMITY_THRESHOLD = 100; // meters

            // Find vehicles within proximity threshold (apenas veículos em linha, consistente com sinótico)
            const nearbyVehicles = this.veiculosPainelEmLinha.filter(veiculo => {
                if (!veiculo.lat || !veiculo.lng || isNaN(veiculo.lat) || isNaN(veiculo.lng)) {
                    return false;
                }

                const distance = haversineMeters(
                    parseFloat(this.lat),
                    parseFloat(this.lng),
                    parseFloat(veiculo.lat),
                    parseFloat(veiculo.lng)
                );

                return distance <= PROXIMITY_THRESHOLD;
            });

            // Generate announcement
            if (nearbyVehicles.length === 1) {
                const veiculo = nearbyVehicles[0];
                const linha = this.linhas.find(l => l.id_linha === veiculo.id_linha);
                const nomeLinha = linha ? linha.nome_linha : 'Linha desconhecida';
                return `Atenção! Veículo ${veiculo.cod_veiculo} da ${nomeLinha} está próximo a você.`;
            } else if (nearbyVehicles.length > 1) {
                return `Atenção! ${nearbyVehicles.length} veículos estão próximos a você.`;
            }

            return '';
        },
    },
    watch: {
        // Manter paradaSelecionadaSinotico em sync quando "priorizar mais próxima" está ativo (evita side effect no computed)
        selectParadaMaisProximaNoSinotico: {
            handler(active) {
                if (active) {
                    this.paradaSelecionadaSinotico = this.paradaMaisProximaSinotico?.parada || null;
                }
            },
            immediate: true,
        },
        paradaMaisProximaSinotico: {
            handler() {
                if (this.selectParadaMaisProximaNoSinotico) {
                    this.paradaSelecionadaSinotico = this.paradaMaisProximaSinotico?.parada || null;
                }
            },
            deep: true,
        },
        paradaSelecionadaPainel(newVal) {
            this.paradaChanged = true;
            if (newVal) {
                this.paradaSelecionadaPainel = newVal;
                this.populaPainel();
            }
        },
        dialogoMapa(newVal) {
            // Manage focus and body scroll when map dialog opens/closes
            if (newVal) {
                // Dialog opened - save current focus to restore later
                this._previousActiveElement = document.activeElement;
                document.body.style.overflow = 'hidden';
                
                // Centralizar mapa e sinótico na parada exibida (do painel ou mais próxima)
                this.$nextTick(() => {
                    this.centralizarParadaExibida();
                });
                
                // Hide content initially and announce opening
                this.dialogJustOpened = true;
                this.dialogAnnouncement = `Navegação pelos pontos da linha: ${this.dialogTitle}, modo navegação ativada`;
                
                // After announcement, unhide content and set focus
                setTimeout(() => {
                    this.dialogJustOpened = false;
                    this.dialogAnnouncement = '';
                    
                    this.$nextTick(() => {
                        // Disable keyboard navigation on map
                        this.disableMapKeyboardNavigation();
                        
                        // Focus on the close button in the dialog
                        const closeBtn = document.querySelector('[aria-label="Fechar"]');
                        if (closeBtn) {
                            closeBtn.focus();
                        }
                    });
                }, 1500);
            } else {
                // Dialog closed - restore focus to element that opened it
                document.body.style.overflow = '';
                this.dialogJustOpened = false;
                this.dialogAnnouncement = '';
                
                // Clean up mutation observer
                if (this._mapObserver) {
                    this._mapObserver.disconnect();
                    this._mapObserver = null;
                }
                
                // Restore focus after dialog is fully closed
                this.$nextTick(() => {
                    if (this._previousActiveElement && this._previousActiveElement.focus) {
                        this._previousActiveElement.focus();
                        this._previousActiveElement = null;
                    }
                });
            }
        },
        linhasDialog(newVal) {
            // Manage focus when lines dialog opens/closes
            if (newVal) {
                document.body.style.overflow = 'hidden';
                this.$nextTick(() => {
                    const closeBtn = document.querySelector('[aria-label="Fechar lista de linhas"]');
                    if (closeBtn) {
                        closeBtn.focus();
                    }
                });
            } else {
                document.body.style.overflow = '';
            }
        },
        linhaSelectedFilter(newVal) {
            // Quando uma linha é selecionada no filtro, buscar viagens previstas
            if (newVal) {
                this.populaViagensPrevistas();
            } else {
                // Se o filtro for limpo, limpar a tabela
                this.viagensPrevistas = [];
            }
        }
    },
    created() {
        this.user = JSON.parse(sessionStorage.getItem("user"));
        this.token = this.user ? sessionStorage.getItem("api") : null;
        if (!this.user) {
            this.token = null;
        }

        if (this.$route.query.lat && this.$route.query.lng) {
            this.lat = parseFloat(this.$route.query.lat);
            this.lng = parseFloat(this.$route.query.lng);
            this.locationAvailable = true;
        }

        if (this.$route.query.fix == 1) {
            this.fixLocation = true;
        }

        setTimeout(() => {
            this.splashMinTimePassed = true;
            this.tryHideSplash();
        }, 3000);

        this.initialize();
    },
    mounted() {
        this.detectPlatform();
        this.setupPWAListeners();

        // setTimeout(() => {
        //     console.log('Executando checkPWAInstall');
        //     this.checkPWAInstall();
        // }, 8000);
    },
    methods: {
        authHeaders() {
            return this.user && this.token
                ? { "x-access-token": this.token }
                : {};
        },
        /** Ordem do veículo na rota: cerca atual → última parada → entre paradas (proxima - 1). Não usa 0 como default. */
        getOrdemVeiculoEfetiva(veiculo) {
            if (!veiculo) return null;
            if (veiculo.ordem_parada_efetiva != null) return Number(veiculo.ordem_parada_efetiva);
            if (veiculo.ordem_parada_atual != null) return Number(veiculo.ordem_parada_atual);
            if (veiculo.ordem_parada_ultima != null) return Number(veiculo.ordem_parada_ultima);
            if (veiculo.ordem_cerca_proxima_parada != null && Number(veiculo.ordem_cerca_proxima_parada) > 1) {
                return Number(veiculo.ordem_cerca_proxima_parada) - 1;
            }
            return null;
        },
        /** Sentido 1-based (1=ida, 2=volta). Painel usa 1/2; veiculosAtivos usa 0/1. */
        getSentidoVeiculo1Based(veiculo) {
            if (!veiculo || veiculo.sentido == null) return null;
            const s = Number(veiculo.sentido);
            if (s >= 1 && s <= 2) return s;
            return s + 1;
        },
        /** Filtra, deduplica e ordena paradas retornadas pela API. */
        processParadasFromApi(data) {
            if (!Array.isArray(data)) return [];
            const filteredParadas = data.filter((r) =>
                r.id_tipo_cerca != 1 && r.id_tipo_cerca != 4
            );
            const seenIds = new Set();
            const uniqueParadas = filteredParadas.filter((parada) => {
                if (seenIds.has(parada.id_cerca)) return false;
                seenIds.add(parada.id_cerca);
                return true;
            });
            return uniqueParadas.sort((a, b) => {
                if (a.id_linha !== b.id_linha) return a.id_linha - b.id_linha;
                if (a.id_sentido !== b.id_sentido) return a.id_sentido - b.id_sentido;
                const isATerminal = a.id_tipo_cerca === 2 || a.id_tipo_cerca === 3;
                const isBTerminal = b.id_tipo_cerca === 2 || b.id_tipo_cerca === 3;
                if (isATerminal && !isBTerminal) return a.id_tipo_cerca === 2 ? -1 : 1;
                if (!isATerminal && isBTerminal) return b.id_tipo_cerca === 2 ? 1 : -1;
                if (isATerminal && isBTerminal) return a.id_tipo_cerca - b.id_tipo_cerca;
                return (a.ordem_parada || 0) - (b.ordem_parada || 0);
            });
        },
        /** Busca paradas de uma linha e armazena em paradasPorLinha (com dedupe de requests). */
        fetchParadasLinha(idLinha) {
            if (!idLinha) return Promise.resolve([]);
            if (this.paradasPorLinha[idLinha]) return Promise.resolve(this.paradasPorLinha[idLinha]);
            if (!this._paradasLinhaPromises) this._paradasLinhaPromises = {};
            if (this._paradasLinhaPromises[idLinha]) return this._paradasLinhaPromises[idLinha];

            const promise = (async () => {
                try {
                    const response = await apiController.request(apiController.endpoints.getCercasporlinhacomvigencia, {
                        data: [idLinha],
                        silent: true,
                    });
                    if (response.data && response.data.erro) {
                        console.error('Erro ao buscar cercas:', response.data.mensagem);
                        return [];
                    }
                    const paradas = this.processParadasFromApi(response.data);
                    this.$set(this.paradasPorLinha, idLinha, paradas);
                    if (this.idLinhaSelected == idLinha) {
                        this.paradas = paradas;
                    }
                    return paradas;
                } catch (e) {
                    console.log(e.message);
                    return [];
                } finally {
                    delete this._paradasLinhaPromises[idLinha];
                }
            })();

            this._paradasLinhaPromises[idLinha] = promise;
            return promise;
        },
        /** Garante paradas em cache para todas as linhas informadas. */
        ensureParadasLinhas(idsLinha) {
            const missing = [...new Set(
                (idsLinha || []).filter((id) => id && !this.paradasPorLinha[id])
            )];
            if (!missing.length) return Promise.resolve();
            return Promise.all(missing.map((id) => this.fetchParadasLinha(id)));
        },
        /** Pré-carrega paradas das linhas exibidas no painel. */
        preloadParadasPainel() {
            const ids = [...new Set(
                this.veiculosPainelEmLinha.map((v) => v.id_linha).filter(Boolean)
            )];
            if (ids.length) this.ensureParadasLinhas(ids);
        },
        /** Parada com ordem_parada alinhada à linha/sentido do veículo. */
        getParadaRefParaVeiculo(veiculo, paradaBase) {
            const parada = paradaBase || this.paradaSelecionadaPainel;
            if (!parada || !veiculo) return parada;
            const sentido = this.getSentidoVeiculo1Based(veiculo);
            const findInList = (list) => list && list.find((p) =>
                p.id_cerca === parada.id_cerca &&
                p.id_linha == veiculo.id_linha &&
                (sentido == null || p.id_sentido == sentido)
            );
            const fromCache = findInList(this.paradasPorLinha[veiculo.id_linha]);
            if (fromCache) return fromCache;
            if (this.idLinhaSelected == veiculo.id_linha) {
                const fromParadas = findInList(this.paradas);
                if (fromParadas) return fromParadas;
            }
            const proximasDaLinha = (this.paradasProximasCompletas.length ? this.paradasProximasCompletas : this.paradasProximas).filter((p) =>
                p.id_cerca === parada.id_cerca && p.id_linha == veiculo.id_linha
            );
            if (proximasDaLinha.length) {
                const porSentido = proximasDaLinha.find((p) => p.id_sentido == sentido);
                return porSentido || proximasDaLinha[0];
            }
            return parada;
        },
        /** Resolve ordem_parada da parada alinhada à linha/sentido do veículo. */
        resolveOrdemParada(parada, veiculo) {
            if (!parada || !veiculo) return null;
            let ordemParada = parada.ordem_parada != null ? Number(parada.ordem_parada) : null;
            if (ordemParada != null && ordemParada > 0) return ordemParada;
            const sentido = this.getSentidoVeiculo1Based(veiculo);
            const cached = this.paradasPorLinha[veiculo.id_linha] || [];
            const resolved = cached.find((p) =>
                p.id_cerca === parada.id_cerca &&
                (sentido == null || p.id_sentido == sentido)
            );
            if (resolved && resolved.ordem_parada != null) {
                return Number(resolved.ordem_parada);
            }
            return null;
        },
        /** Veículo já passou pela parada (não está mais nela). Enquanto ainda não passou, retorna false. */
        veiculoPassouParada(veiculo, parada) {
            if (!veiculo || !parada) return false;
            const p = this.getParadaRefParaVeiculo(veiculo, parada);
            if (!p) return false;
            const noPonto = veiculo.id_cerca != null && Number(veiculo.id_cerca) === Number(p.id_cerca);
            if (noPonto) return false;
            if (veiculo.id_cerca_ultima_parada != null && Number(veiculo.id_cerca_ultima_parada) === Number(p.id_cerca)) {
                return true;
            }
            const ordemParada = this.resolveOrdemParada(p, veiculo);
            if (ordemParada == null || ordemParada <= 0) return false;
            if (veiculo.ordem_parada_ultima != null && Number(veiculo.ordem_parada_ultima) > ordemParada) return true;
            if (veiculo.ordem_parada_atual != null && Number(veiculo.ordem_parada_atual) > ordemParada) return true;
            if (veiculo.ordem_cerca_proxima_parada != null && Number(veiculo.ordem_cerca_proxima_parada) > ordemParada
                && veiculo.id_cerca_proxima_parada != null && Number(veiculo.id_cerca_proxima_parada) !== Number(p.id_cerca)) {
                return true;
            }
            const ordemVeiculo = this.getOrdemVeiculoEfetiva(veiculo);
            return ordemVeiculo != null && ordemVeiculo > ordemParada;
        },
        /** Exibir veículo no painel: sim enquanto não passou; sem viagem aberta permanece visível enquanto retornado pela API. */
        veiculoVisivelNoPainel(veiculo) {
            if (!veiculo || veiculo.id_viagem == null) return true;
            const parada = this.getParadaRefParaVeiculo(veiculo);
            if (!parada) return true;
            return !this.veiculoPassouParada(veiculo, parada);
        },
        /** ETA da API getveiculospainel para a parada consultada. */
        getPainelEtaParada(veiculo, parada) {
            if (!veiculo || !parada || veiculo.has_eta !== 1 || this.veiculoPassouParada(veiculo, parada)) return null;
            if (veiculo.eta_segundos_parada_painel == null) return null;
            const eta = Number(veiculo.eta_segundos_parada_painel);
            return Number.isFinite(eta) && eta >= 0 ? eta : null;
        },
        /** ETA em segundos para exibição no card do painel; null quando deve mostrar "Calculando...". */
        resolveEtaSegundosPainelCard(veiculo, p, idLinha, idSentidoETA, isPontoFinalDoSentido) {
            if (isPontoFinalDoSentido) {
                if (veiculo.eta_segundos_parada_final != null) return Number(veiculo.eta_segundos_parada_final);
                if (veiculo.has_eta !== 1) return null;
                return this.getETAVeiculoParada(veiculo.id_veiculo, p.id_cerca, idLinha, idSentidoETA, veiculo)
                    ?? this.getETAFromCacheOnly(veiculo.id_veiculo, p.id_cerca, idLinha, idSentidoETA);
            }
            const etaAtivo = this.getETAVeiculoParada(veiculo.id_veiculo, p.id_cerca, idLinha, idSentidoETA, veiculo);
            if (etaAtivo != null) return etaAtivo;
            if (veiculo.has_eta !== 1) return null;
            return this.getETAFromCacheOnly(veiculo.id_veiculo, p.id_cerca, idLinha, idSentidoETA)
                ?? this.getPainelEtaParada(veiculo, p);
        },
        AjusteETA(veiculo, etaSegundos) {
            const secondsFromLastUpdate = Math.floor((Date.now() - new Date(veiculo.dt_atualizacao_eta).getTime()) / 1000);
            const etaSegundosRestantes = etaSegundos - secondsFromLastUpdate > 0 ? etaSegundos - secondsFromLastUpdate : 0;
            return etaSegundosRestantes;
        },
        navigateToHome() {
            this.currentView = 'home';
        },
        navigateToViagensPrevistas() {
            this.currentView = 'viagensPrevistas';
        },
        async carregarETAParadas(idLinha, idSentido) {
            // Cria chave única para cache
            const cacheKey = `${idLinha}_${idSentido}`;
            
            try {
                const response = await apiController.request(apiController.endpoints.getEtaparadaslinha, {
                    params: { idLinha, idSentido },
                    silent: true,
                });

                // Criar mapa de ETAs: { 'id_veiculo_id_cerca': eta_segundos } (incl. 0 para veículo no ponto)
                const etaMap = {};
                if (response.data && Array.isArray(response.data)) {
                    response.data.forEach(item => {
                        const etaSegundos = Number(item.eta_segundos);
                        if (!Number.isFinite(etaSegundos) || etaSegundos < 0) return;
                        const key = `${item.id_veiculo}_${item.id_cerca}`;
                        etaMap[key] = etaSegundos;
                    });
                }
                
                // Armazenar no cache
                this.$set(this.etaParadasCache, cacheKey, etaMap);
                
                return etaMap;
                
            } catch (error) {
                console.error('Erro ao carregar ETA das paradas:', error);
                return {};
            }
        },
        getETAVeiculoParada(idVeiculo, idCerca, idLinha, idSentido, veiculo) {
            // has_eta=0: não retornar ETA para nenhum ponto da linha
            if (veiculo && veiculo.has_eta !== 1) return null;
            // Quando a parada é a próxima do veículo, usar eta_segundos_proxima_parada; senão cache da API
            if (veiculo && veiculo.id_cerca_proxima_parada != null && Number(veiculo.id_cerca_proxima_parada) === Number(idCerca)) {
                const eta = veiculo.eta_segundos_proxima_parada;
                if (eta != null && Number(eta) >= 0) return Number(eta);
            }
            const cacheKey = `${idLinha}_${idSentido}`;
            const etaKey = `${idVeiculo}_${idCerca}`;
            const cached = this.etaParadasCache[cacheKey]?.[etaKey];
            if (cached != null && Number.isFinite(Number(cached))) {
                return Number(cached);
            }
            return null;
        },
        /** Retorna ETA apenas do cache (sem checar has_eta). Usado em getTempoChegadaParadasSinoticoCard para considerar todos os veículos em rota com ETA no cache. */
        getETAFromCacheOnly(idVeiculo, idCerca, idLinha, idSentido) {
            const cacheKey = `${idLinha}_${idSentido}`;
            const etaKey = `${idVeiculo}_${idCerca}`;
            const val = this.etaParadasCache[cacheKey]?.[etaKey];
            if (val != null && Number.isFinite(Number(val))) {
                return Number(val);
            }
            return null;
        },
        getHoraAtual(){
            return new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        },
        populaViagensPrevistas() {
            // Busca o id_linha da linha selecionada
            const linhaSelecionada = this.linhas.find(l => l.nome_linha === this.linhaSelectedFilter);
            
            if (!linhaSelecionada) {
                this.viagensPrevistas = [];
                return;
            }

            apiController.request(apiController.endpoints.getViagensprevistaslinha, {
                data: [linhaSelecionada.id_linha],
                silent: true,
                onSuccess: (response) => {
                    // Processar os dados retornados
                    if (Array.isArray(response.data)) {
                        this.viagensPrevistas = response.data.filter(viagem => {
                            const horaAtual = this.getHoraAtual();
                            return (viagem.horario_chegada >= horaAtual)
                            && (viagem.id_sentido == 1 || viagem.id_sentido == 2)
                            && (viagem.descricao_sentido == 'Ida' ? viagem.id_tipo_cerca == 3 : viagem.id_tipo_cerca == 2);
                        }).map(viagem => {
                            return {
                                hora_prevista_saida: viagem.horario_saida,
                                hora_prevista_chegada: viagem.horario_chegada,
                                sentido: viagem.nome_cerca,
                                descricao_viagem: viagem.descricao_viagem,
                                id_viagem: viagem.id_viagem
                            };
                        }).sort((a, b) => (a.hora_prevista_saida || '').localeCompare(b.hora_prevista_saida || ''));

                    } else {
                        this.viagensPrevistas = [];
                        console.warn('Resposta da API não é um array:', response.data);
                    }
                },
                onError: (e) => {
                    this.viagensPrevistas = [];
                    EventBus.$emit('show-alert', {
                        mensagem: this.$t('consultationPassenger.failedToRetrieveRoutes') || 'Erro ao buscar viagens previstas',
                        tipo: 'error'
                    });
                    console.error('Erro ao buscar viagens previstas:', e.message);
                },
            });
        },
        tryHideSplash() {
            if (this.splashMinTimePassed) {
                this.showSplash = false;
            }
        },
        checkSplashDataLoaded() {
            this.splashDataLoaded = true;
            this.tryHideSplash();
        },
        initialize() {
            this.populaLinhas();
            this.populaAvisos();
            this.atualizaDados();
        },
        gravaAcessoLatLng() {
            if (this.lat && this.lng) {
                apiController.request(apiController.endpoints.insertAcessowebapplatlng, {
                    data: { lat: this.lat, lng: this.lng, user: this.user },
                    onSuccess: (response) => {
                        this.idAcesso = response.data.id_acesso;
                    },
                });
            }
        },
        gravaAcessoLinha() {
            if (this.idLinhaSelected && this.idAcesso) {
                apiController.request(apiController.endpoints.insertAcessowebapplinha, {
                    data: { id_acesso: this.idAcesso, id_linha: this.idLinhaSelected, user: this.user },
                });
            }
        },
        populaVeiculosAtivos() {
            apiController.request(apiController.endpoints.getVeiculosconsultapassageiro, {
                data: { id: (this.user) ? this.user.id_usuario : null, tipo: getIdModo() },
                silent: true,
                onSuccess: (response) => {
                    this.veiculosAtivos = response.data;
                    this.$nextTick(() => this.preloadParadasPainel());
                },
                onError: (e) => {
                    console.log(e.message);
                },
            });
        },
        populaAvisos() {
            apiController.request(apiController.endpoints.getAvisosvigentesbannerbyid, {
                params: { id_banner: 1 },
                silent: true,
                onSuccess: (response) => {
                    this.avisos = response.data;
                },
                onError: (e) => {
                    console.log(e.message);
                },
            });
        },
        populaLinhas() {
            this.loadingLinhas = true;
            apiController.request(apiController.endpoints.getLinhasconsultapassageiro, {
                data: { id: (this.user) ? this.user.id_usuario : null, tipo: getIdModo() },
                silent: true,
                onSuccess: (response) => {
                    this.linhas = response.data;
                    this.populaRotas();
                    this.loadingLinhas = false;
                },
                onError: (e) => {
                    this.loadingLinhas = false;
                    EventBus.$emit('show-alert', {
                        mensagem: this.$t('consultationPassenger.failedToRetrieveLines'),
                        tipo: 'error'
                    });
                    console.log(e.message);
                },
            });
        },
        populaRotas() {
            this.loadingRotas = true;
            apiController.request(apiController.endpoints.getRotasporlinhacomvigencia, {
                data: this.linhas.map((l) => l.id_linha),
                silent: true,
                onSuccess: (response) => {
                    // Check if response is valid
                    if (response.data && response.data.erro) {
                        console.error('Erro ao buscar rotas:', response.data.mensagem);
                        this.loadingRotas = false;
                        return;
                    }

                    // Ensure response.data is an array
                    if (!Array.isArray(response.data)) {
                        console.error('Resposta inválida da API - esperado array:', response.data);
                        this.rotas = [];
                        this.loadingRotas = false;
                        return;
                    }

                    // Filter: tipo_rota 1, sentido 1 or 2
                    this.rotas = response.data.filter((r) =>
                        r.id_tipo_rota == 1 &&
                        (r.id_sentido == 1 || r.id_sentido == 2)
                    ).map((r) => {
                        r.geojson = JSON.parse(r.geojson);
                        return r;
                    });

                    console.log(`🗺️ Loaded ${this.rotas.length} routes`);
                    this.loadingRotas = false;
                },
                onError: (e) => {
                    this.loadingRotas = false;
                    EventBus.$emit('show-alert', {
                        mensagem: this.$t('consultationPassenger.failedToRetrieveRoutes'),
                        tipo: 'error'
                    });
                    console.log(e.message);
                },
            });
        },
        populaParadas(idLinha) {
            const linha = idLinha != null ? idLinha : this.idLinhaSelected;
            if (!linha) return;
            this.fetchParadasLinha(linha)
                .then((paradas) => {
                    if (this.idLinhaSelected == linha) {
                        this.paradas = paradas;
                    }
                    if (this.dialogoMapa) {
                        this.$nextTick(() => {
                            this.centralizarParadaExibida();
                        });
                    }
                })
                .catch((e) => {
                    EventBus.$emit('show-alert', {
                        mensagem: this.$t('consultationPassenger.failedToRetrieveLines'),
                        tipo: 'error'
                    });
                    console.log(e.message);
                });
        },
        getNomeLinha(id) {
            const linha = this.linhas.find(l => l.id_linha === id);

            if (linha) {
                return `${linha.nome_linha}`;
            } else {
                return "";
            }
        },
        /** Formata ETA em segundos para exibição (usado no card do sinótico, parada do sinótico). */
        formatETAPorSegundos(etaSegundos, noPonto) {
            if (noPonto) {
                return 'No ponto';
            }
            const minutos = Math.floor(etaSegundos / 60);
            if (minutos < 1) return this.$t('consultationPassenger.arrivingNow');
            if (minutos < 60) return `Chega em: ${minutos} min`;
            if (minutos < 120) return 'Mais de 1 hora';
            if (minutos < 180) return 'Mais de 2 horas';
            return 'Mais de 3 horas';
        },
        /** Retorna ETA em segundos do veículo para a parada do painel (para ordenação). Null se "Calculando...". */
        getETASegundosParaParadaPainel(veiculo) {
            const p = this.getParadaRefParaVeiculo(veiculo);
            if (!veiculo || !p) return null;
            if (this.veiculoPassouParada(veiculo, p)) return null;
            const noPonto = veiculo.id_cerca != null && Number(veiculo.id_cerca) === Number(p.id_cerca);
            if (noPonto) return 0;
            const idLinha = this.idLinhaSelected || veiculo.id_linha;
            if (!idLinha) return null;
            const isPontoFinalDoSentido = p.id_tipo_cerca === 2 && veiculo.sentido === 2 || p.id_tipo_cerca === 3 && veiculo.sentido === 1;
            const idSentidoETA = isPontoFinalDoSentido ? -1 : (p.id_sentido != null ? p.id_sentido : veiculo.sentido);
            const etaSegundos = this.resolveEtaSegundosPainelCard(veiculo, p, idLinha, idSentidoETA, isPontoFinalDoSentido);
            if (etaSegundos == null || !Number.isFinite(etaSegundos) || etaSegundos < 0) return null;
            const etaAjustado = veiculo.dt_atualizacao_eta ? this.AjusteETA(veiculo, etaSegundos) : etaSegundos;
            return etaAjustado >= 0 ? etaAjustado : null;
        },
        /** ETA do veículo para a parada selecionada no painel (paradaSelecionadaPainel). Usado no card do painel. O veiculo vem de veiculosPainelEmLinha. */
        getTempoChegadaParadasPainelCard(veiculo, parada) {
            if (!veiculo || !parada) return "Calculando...";

            const p = parada.id_cerca != null ? this.getParadaRefParaVeiculo(veiculo, parada) : this.paradaSelecionadaPainel;
            if (!p) return "Calculando...";
            if (this.veiculoPassouParada(veiculo, p)) return "Calculando...";

            const noPonto = veiculo.id_cerca != null && Number(veiculo.id_cerca) === Number(p.id_cerca);
            if (noPonto) return this.formatETAPorSegundos(0, true);

            if (veiculo.has_eta != 1) {
                if (veiculo.sem_comunicacao == 1) {
                    return this.$t('consultationPassenger.noRealtimeLocation');
                }
                if (veiculo.id_viagem == null) {
                    return this.$t('consultationPassenger.scheduledNoEta');
                }
                return this.$t('consultationPassenger.realtimeEtaUnavailable');
            }

            const idLinha = this.idLinhaSelected || veiculo.id_linha;
            if (!idLinha) return "Calculando...";

            const isPontoFinalDoSentido = p.id_tipo_cerca === 2 && veiculo.sentido === 2 || p.id_tipo_cerca === 3 && veiculo.sentido === 1;
            const idSentidoETA = isPontoFinalDoSentido ? -1 : (p.id_sentido != null ? p.id_sentido : veiculo.sentido);

            const etaSegundos = this.resolveEtaSegundosPainelCard(veiculo, p, idLinha, idSentidoETA, isPontoFinalDoSentido);

            if (etaSegundos == null || !Number.isFinite(etaSegundos) || etaSegundos < 0) return "Calculando...";
            const etaAjustado = veiculo.dt_atualizacao_eta ? this.AjusteETA(veiculo, etaSegundos) : etaSegundos;
            if (etaAjustado < 0) return "Calculando...";

            const proximaEhParada = veiculo.id_cerca_proxima_parada != null
                && Number(veiculo.id_cerca_proxima_parada) === Number(p.id_cerca);
            if (etaAjustado < 60 && !proximaEhParada) return "Calculando...";

            return this.formatETAPorSegundos(etaAjustado, false);
        },
        getTempoChegadaClass(veiculo) {
            if (veiculo.has_eta == 1) {
                return "eta__available";
            }
            return "eta__unavailable";
        },
        getTempoIcon(veiculo) {
            if (veiculo.has_eta == 1) {
                return "mdi-wifi";
            }
            return "mdi-clock-outline";
        },
        async onLinhaChange() {
            this.populaParadas();
            this.gravaAcessoLinha();
            
            // Carregar ETAs para ambos os sentidos e paradas finais (id_sentido = -1) da linha selecionada
            if (this.idLinhaSelected) {
                await Promise.all([
                    this.carregarETAParadas(this.idLinhaSelected, 1),  // Ida
                    this.carregarETAParadas(this.idLinhaSelected, 2),  // Volta
                    this.carregarETAParadas(this.idLinhaSelected, -1)  // Paradas finais (terminais)
                ]);
            }
        },
        async atualizaDados() {
            // Sempre atualizar veículos ativos
            this.populaVeiculosAtivos();
            
            // Atualizar ETAs da linha selecionada se houver (ida, volta e paradas finais)
            if (this.idLinhaSelected) {
                await Promise.all([
                    this.carregarETAParadas(this.idLinhaSelected, 1),  // Ida
                    this.carregarETAParadas(this.idLinhaSelected, 2),  // Volta
                    this.carregarETAParadas(this.idLinhaSelected, -1) // Paradas finais (terminais)
                ]);
            }

            if (!this.fixLocation) {
                // Verificar se a localização está disponível
                if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition((pos) => {
                        this.lat = pos.coords.latitude;
                        this.lng = pos.coords.longitude;

                        if (!this.dialogoMapa) {
                            this.center = [this.lat, this.lng];
                        }
                        this.locationAvailable = true;

                        // Atualizar painel com localização
                        this.populaPainel();
                        if (!this.acessoRegistrado) {
                                this.gravaAcessoLatLng();
                                this.acessoRegistrado = true;
                            }
                    }, (error) => {
                        if (this.lat && this.lng) {
                            this.locationAvailable = true;
                            if (!this.dialogoMapa) {
                                this.center = [this.lat, this.lng];
                            }
                            this.populaPainel();
                            if (!this.acessoRegistrado) {
                                this.gravaAcessoLatLng();
                                this.acessoRegistrado = true;
                            }
                        } else {
                            this.locationAvailable = false;
                            console.warn("Geolocation error:", error);
                            
                            EventBus.$emit('show-alert', {
                                mensagem: `${this.$t('consultationPassenger.locationNotAvailable')}. ${this.$t('consultationPassenger.description')}`,
                                tipo: 'warning',
                                duracao: 8000
                            });
                            

                            // Apenas atualizar veículos ativos se não tiver localização
                            this.checkSplashDataLoaded();
                        }
                    });
                } else {
                    this.locationAvailable = false;
                    console.warn("Geolocation is not supported by your browser.");
                    
                    EventBus.$emit('show-alert', {
                        mensagem: `${this.$t('consultationPassenger.locationNotSupported')}. ${this.$t('consultationPassenger.description2')}`,
                        tipo: 'warning',
                        duracao: 8000
                    });
                    
                    this.checkSplashDataLoaded();
                }
            } else {
                this.populaPainel();
            }

            if (this.timeout) {
                clearTimeout(this.timeout);
            }
            this.timeout = setTimeout(this.atualizaDados, 30000);
        },
        dedupeParadasPorCerca(lista) {
            const seen = new Set();
            return lista.filter((p) => {
                if (seen.has(p.id_cerca)) return false;
                seen.add(p.id_cerca);
                return true;
            });
        },
        async populaPainel() {
            this.loading = true;

            let body = {
                coords: {
                    type: 'Circle',
                    center: {
                        lat: this.lat,
                        lng: this.lng
                    },
                    radius: this.raio
                }
            }

            try {
                const response = await apiController.request(apiController.endpoints.getCercamaisproxima, {
                    data: body,
                    silent: true,
                });
                const lista = Array.isArray(response.data) ? response.data : [];
                this.paradasProximasCompletas = lista;
                this.paradasProximas = this.dedupeParadasPorCerca(lista);
                if (this.pendingParadaSelecionadaId) {
                    const paradaPendente = this.paradasProximas.find(p => p.id_cerca === this.pendingParadaSelecionadaId);
                    if (paradaPendente) {
                        this.paradaSelecionadaPainel = paradaPendente;
                        this.paradaChanged = true;
                    }
                    this.pendingParadaSelecionadaId = null;
                }
                if (!this.paradaChanged && !this.paradaSelecionadaPainel && this.paradasProximas.length > 0) {
                    this.paradaSelecionadaPainel = this.paradasProximas[0];
                }
                if (!this.paradaSelecionadaPainel) {
                    this.loading = false;
                    this.checkSplashDataLoaded();
                    return;
                }
            } catch (e) {
                this.loading = false;
                this.checkSplashDataLoaded();
                EventBus.$emit('show-error', { error: e });
                return;
            }

            try {
                const response = await apiController.request(apiController.endpoints.getVeiculospainel, {
                    data: { id_cerca: this.paradaSelecionadaPainel.id_cerca, lat: this.lat, lng: this.lng },
                    silent: true,
                });
                this.veiculos = response.data;
                this.veiculos = this.veiculos.sort(this.sortVeiculos);
                this.preloadParadasPainel();

                this.loading = false;
                this.checkSplashDataLoaded();
            } catch (e) {
                this.loading = false;
                this.checkSplashDataLoaded();
                EventBus.$emit('show-error', { error: e });
            }
        },
        sortVeiculos(a, b) {
            if (this.paradaSelecionadaPainel) {

                const paradaCoords = {
                    latitude: parseFloat(this.paradaSelecionadaPainel.lat),
                    longitude: parseFloat(this.paradaSelecionadaPainel.lng)
                };

                const aHasCoords = a.lat != null && a.lng != null && !isNaN(a.lat) && !isNaN(a.lng);
                const bHasCoords = b.lat != null && b.lng != null && !isNaN(b.lat) && !isNaN(b.lng);

                if (aHasCoords && bHasCoords) {
                    const veiculoACoords = {
                        latitude: parseFloat(a.lat),
                        longitude: parseFloat(a.lng)
                    };
                    const veiculoBCoords = {
                        latitude: parseFloat(b.lat),
                        longitude: parseFloat(b.lng)
                    };

                    const distanciaA = GeoLib.getDistance(paradaCoords, veiculoACoords);
                    const distanciaB = GeoLib.getDistance(paradaCoords, veiculoBCoords);

                    return distanciaA - distanciaB;
                }

                if (aHasCoords && !bHasCoords) {
                    return -1;
                }
                if (!aHasCoords && bHasCoords) {
                    return 1;
                }
            }

            return 0;
        },
        /** Cor do card pela regra de ETA: vermelho (longa espera), amarelo (calculando), cinza (sem ETA), verde (demais). */
        getCardColor(veiculo) {
            if (!veiculo) return "#FFFFFF";
            const parada = this.paradaSelecionadaPainel;
            const tempo = this.getTempoChegadaParadasPainelCard(veiculo, parada);
            if (tempo === "No ponto") return "#73fa57";
            if (veiculo.has_eta != 1) return "#B0BEC5";
            if (["Mais de 1 hora", "Mais de 2 horas", "Mais de 3 horas"].includes(tempo)) return "#fc6262";
            if (tempo === "Calculando...") return "#ffee8c";
            return "#73fa57";
        },

        getIcon(v) {
            let color = "none";
            let width = "25";
            let height = "25";
            let classname = "my-custom-pin";

            if (v.sentido === 0) {
                color = "#008000";
            } else if (v.sentido === 1) {
                color = "orange";
            } else {
                color = "#4a4a4a";
            }

            if (v.gps_offline === 1) {
                classname += " blink";
            }

            return L.divIcon({
                className: classname,
                html: `<div class="vehicle-icon-container">
                        <svg fill="${color}" width="${width}" height="${height}" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                            <path d="M12 0C5.4375 0 3 2.167969 3 8L3 41C3 42.359375 3.398438 43.339844 4 44.0625L4 47C4 48.652344 5.347656 50 7 50L11 50C12.652344 50 14 48.652344 14 47L14 46L36 46L36 47C36 48.652344 37.347656 50 39 50L43 50C44.652344 50 46 48.652344 46 47L46 44.0625C46.601563 43.339844 47 42.359375 47 41L47 9C47 4.644531 46.460938 0 40 0 Z M 15 4L36 4C36.554688 4 37 4.449219 37 5L37 7C37 7.550781 36.554688 8 36 8L15 8C14.449219 8 14 7.550781 14 7L14 5C14 4.449219 14.449219 4 15 4 Z M 11 11L39 11C41 11 42 12 42 14L42 26C42 28 40.046875 28.9375 39 28.9375L11 29C9 29 8 28 8 26L8 14C8 12 9 11 11 11 Z M 2 12C0.898438 12 0 12.898438 0 14L0 22C0 23.101563 0.898438 24 2 24 Z M 48 12L48 24C49.105469 24 50 23.101563 50 22L50 14C50 12.898438 49.105469 12 48 12 Z M 11.5 34C13.433594 34 15 35.566406 15 37.5C15 39.433594 13.433594 41 11.5 41C9.566406 41 8 39.433594 8 37.5C8 35.566406 9.566406 34 11.5 34 Z M 38.5 34C40.433594 34 42 35.566406 42 37.5C42 39.433594 40.433594 41 38.5 41C36.566406 41 35 39.433594 35 37.5C35 35.566406 36.566406 34 38.5 34Z"/>
                        </svg>
                    </div>`,
                iconAnchor: [12, 25]
            });
        },
        getTimestampIcon(veiculo) {
            return L.divIcon({
                className: "timestamp-badge-icon",
                html: `<div class="timestamp-badge-content">${veiculo.dt_gps}</div>`,
                iconAnchor: [12, 25]
            });
        },
        getIconParada() {
            return L.divIcon({
                className: "parada-circle-pin",
                html: `<svg width="15" height="15" viewBox="0 0 20 20">
                        <circle cx="10" cy="10" r="8" fill="rgb(252, 98, 98)" stroke="#0909e3" stroke-width="2"/>
                    </svg>`,
                iconAnchor: [8, 8]
            });
        },
        getUserIcon() {
            return L.divIcon({
                className: 'user_pin',
                html: '<svg fill="#0284cf" width="70px" height="70px" viewBox="0 0 32 32" role="img" focusable="false" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M 4.78158,2.71429 C 4.78158,1.76752 5.5491,1 6.49587,1 c 0.94677,0 1.71428,0.76752 1.71428,1.71429 0,0.94676 -0.76751,1.71428 -1.71428,1.71428 -0.94677,0 -1.71429,-0.76752 -1.71429,-1.71428 z m 3,2.14285 -0.30428,0 c -0.60833,0.27973 -1.32831,0.29181 -1.96286,0 l -0.30429,0 c -0.71009,0 -1.28571,0.57563 -1.28571,1.28572 l 0,2.78571 c 0,0.35505 0.28781,0.64286 0.64286,0.64286 l 0.42857,0 0,2.78571 C 4.99587,12.71219 5.28368,13 5.63873,13 l 1.71428,0 c 0.35505,0 0.64286,-0.28781 0.64286,-0.64286 l 0,-2.78571 0.42857,0 c 0.35504,0 0.64286,-0.28781 0.64286,-0.64286 l 0,-2.78571 c 0,-0.71009 -0.57563,-1.28572 -1.28572,-1.28572 z m 2.29398,5.06657"/></svg>'
            });
        },
        latLng(lat, lng) {
            if (lat && lng) {
                if (typeof lat !== "string") {
                    lat = lat.toString();
                }

                if (typeof lng !== "string") {
                    lng = lng.toString();
                }

                if (lat.trim() !== "0") {
                    return L.latLng(lat, lng);
                } else {
                    return L.latLng(-22.88746, -43.29185);
                }
            } else {
                return L.latLng(-22.88746, -43.29185);
            }
        },
        getPolylineStyle() {
            return {
                weight: 2,
                color: "#0909e3",
                opacity: 1
            };
        },
        fechaDialogo() {
            this.dialogoMapa = false;
            this.paradaSelecionadaSinotico = null; // Reset parada selecionada
            this.selectParadaMaisProximaNoSinotico = false;
        },
        selecionarLinha(veiculo) {
            this.veiculoSelecionadoPainel = veiculo;
            this.idLinhaSelected = veiculo.id_linha;
            this.onLinhaChange();
            // Mostrar no card do sinótico a parada escolhida no select do painel
            this.paradaSelecionadaSinotico = this.paradaSelecionadaPainel;

            this.abreDialogo(veiculo);
        },
        selecionarLinhaDoDialog(linha) {
            this.idLinhaSelected = linha.id_linha;
            this.onLinhaChange();
            this.linhasDialog = false;
            // Priorizar parada mais próxima do usuário no card (não paradaSelecionadaPainel)
            this.selectParadaMaisProximaNoSinotico = true;
            this.paradaSelecionadaSinotico = this.paradaMaisProximaSinotico?.parada || null;
            this.abreDialogo(null);
            // Centralizar na parada mais próxima quando o diálogo e as paradas estiverem disponíveis
            this.$nextTick(() => {
                this.$nextTick(() => this.centralizarParadaExibida());
            });
        },
        abreDialogo(veiculo) {
            // Só zera parada do sinótico e veículo selecionado quando abrir pela lista de linhas (veiculo null); no clique do card já foi setada em selecionarLinha
            if (veiculo === null) {
                this.paradaSelecionadaSinotico = null;
                this.veiculoSelecionadoPainel = null;
            }
            
            // Prepare the dialog state
            const wasOpen = this.dialogoMapa;
            
            // Update key to force re-render
            this.key = this.key == 0 ? 1 : 0;
            
            // Set direction based on vehicle
            if (veiculo) {
                if (veiculo.sentido === 1) {
                    // Vehicle is in "Ida" direction (sentido 1)
                    this.idaDisabled = false;  // Enable Ida (show Ida routes)
                    this.voltaDisabled = true;  // Disable Volta
                } else if (veiculo.sentido === 2) {
                    // Vehicle is in "Volta" direction (sentido 2)
                    this.idaDisabled = true;   // Disable Ida
                    this.voltaDisabled = false; // Enable Volta (show Volta routes)
                } else {
                    // Default to Ida for other sentido values
                    this.idaDisabled = false;
                    this.voltaDisabled = true;
                }
            } else {
                // Default to Ida if no vehicle specified
                this.idaDisabled = false;
                this.voltaDisabled = true;
            }

            // Update keySinotico to reflect the current direction
            this.keySinotico = this.idaDisabled ? 'sin-2' : 'sin-1';
            
            // If dialog was already open, close it first then reopen
            if (wasOpen) {
                this.dialogoMapa = false;
                this.$nextTick(() => {
                    this.dialogoMapa = true;
                });
            } else {
                // Just open it directly
                this.dialogoMapa = true;
            }
        },
        onMapResize() {
            this.$refs.map.mapObject.invalidateSize();
            this.disableMapKeyboardNavigation();
        },
        disableMapKeyboardNavigation() {
            // Disable keyboard navigation on all map elements
            this.$nextTick(() => {
                const mapContainer = document.getElementById('mapcontainer');
                if (mapContainer) {
                    // Remove tabindex from all focusable elements in the map
                    const focusableElements = mapContainer.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])');
                    focusableElements.forEach(element => {
                        element.setAttribute('tabindex', '-1');
                    });
                    
                    // Set up a MutationObserver to catch dynamically added elements
                    if (!this._mapObserver) {
                        this._mapObserver = new MutationObserver(() => {
                            const newFocusableElements = mapContainer.querySelectorAll('a:not([tabindex="-1"]), button:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])');
                            newFocusableElements.forEach(element => {
                                element.setAttribute('tabindex', '-1');
                            });
                        });
                        this._mapObserver.observe(mapContainer, {
                            childList: true,
                            subtree: true
                        });
                    }
                }
            });
        },
        getSentido(sentido) {
            if (sentido == 1) {
                return this.$t('operation.go');
            } else if (sentido == 2) {
                return this.$t('operation.back');
            }

            return "";
        },
        requestLocation() {
            if (!this.fixLocation) {
                this.requestingLocation = true;
                if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                        (position) => {
                            this.lat = position.coords.latitude;
                            this.lng = position.coords.longitude;
                            if (!this.dialogoMapa) {
                                this.center = [this.lat, this.lng];
                            }
                            this.locationAvailable = true;
                            this.atualizaDados();
                            this.requestingLocation = false;
                        },
                        (error) => {
                            this.locationAvailable = false;
                            this.requestingLocation = false;
                            console.warn("Geolocation error:", error);
                            EventBus.$emit('show-alert', {
                                mensagem: this.$t('consultationPassenger.locationNot'),
                                tipo: 'error'
                            });
                        }
                    );
                } else {
                    this.locationAvailable = false;
                    this.requestingLocation = false;
                    console.warn("Geolocation is not supported by your browser.");
                    EventBus.$emit('show-alert', {
                        mensagem: this.$t('consultationPassenger.locationNotSupported'),
                        tipo: 'error'
                    });
                }
            }
        },
        irParaTroubleshooting() {
            window.open('/troubleshooting-localizacao', '_blank');
        },
        sentidoChanged() {
            this.keySinotico = this.keySinotico === 'sin-1' ? 'sin-2' : 'sin-1';
            this.idaDisabled = !this.idaDisabled;
            this.voltaDisabled = !this.voltaDisabled;
            this.paradaSelecionadaSinotico = this.paradaMaisProximaSinotico?.parada || null;

            // Após trocar o sentido, centralizar o mapa no ponto selecionado (mais próximo do novo sentido) e garantir tooltip
            this.$nextTick(() => {
                this.centralizarParadaExibida();
            });
        },
        onParadaSelecionada(parada) {
            // Ao alterar a parada no sinótico, usar sempre o veículo com menor ETA (não o do card selecionado no painel)
            this.veiculoSelecionadoPainel = null;
            // Apenas atualiza a parada exibida no sinótico/diálogo; não altera o select do painel (paradaSelecionadaPainel)
            this.selectParadaMaisProximaNoSinotico = false; // Usuário escolheu uma parada
            this.paradaSelecionadaSinotico = parada;

            // Move map to selected stop location
            if (parada && parada.lat_centro && parada.lng_centro) {
                const lat = parseFloat(parada.lat_centro);
                const lng = parseFloat(parada.lng_centro);
                
                if (!isNaN(lat) && !isNaN(lng)) {
                    // Update the map center
                    this.center = [lat, lng];
                    
                    // If map reference exists, pan to the location with animation
                    this.$nextTick(() => {
                        if (this.$refs.map && this.$refs.map.mapObject) {
                            this.$refs.map.mapObject.setView([lat, lng], 16, {
                                animate: true,
                                duration: 1
                            });
                        }
                    });
                }
            }
        },
        centralizarParadaExibida() {
            // Centraliza mapa na parada exibida no card (incl. ponto final do painel)
            const parada = (this.paradaSinoticoCard && (this.paradaSinoticoCard.parada || this.paradaSinoticoCard)) || null;
            if (!parada) return;
            
            // Aceitar lat/lng (painel - getcercamaisproxima) ou lat_centro/lng_centro (sinótico - getcercasporlinha)
            const latVal = parada.lat_centro != null ? parada.lat_centro : parada.lat;
            const lngVal = parada.lng_centro != null ? parada.lng_centro : parada.lng;
            if (latVal != null && lngVal != null) {
                const lat = parseFloat(latVal);
                const lng = parseFloat(lngVal);
                
                if (!isNaN(lat) && !isNaN(lng)) {
                    this.center = [lat, lng];
                    
                    this.$nextTick(() => {
                        if (this.$refs.map && this.$refs.map.mapObject) {
                            this.$refs.map.mapObject.setView([lat, lng], 17, {
                                animate: true,
                                duration: 1
                            });
                        }
                    });
                }
            }
            
            // Centralizar sinótico
            this.$nextTick(() => {
                if (this.$refs.sinotico && this.$refs.sinotico.centralizarScrollNoPonto) {
                    this.$refs.sinotico.centralizarScrollNoPonto(parada);
                }
            });
        },
        irParaTroubleshootingPWA() {
            window.open('/troubleshooting-pwa', '_blank');
        },
        installPWA() {
            // Para iOS, sempre mostrar instruções manuais
            if (this.isIOS) {
                this.installingPWA = true;
                this.showPWAInstall = false;

                // Se não for Safari, tentar abrir no Safari
                if (!this.isSafari) {
                    const currentUrl = window.location.href;
                    const safariUrl = `x-web-search://?${currentUrl}`;

                    // Tentar abrir no Safari
                    window.location.href = safariUrl;

                    // Fallback: mostrar instruções
                    setTimeout(() => {
                        EventBus.$emit('show-alert', {
                            mensagem: this.$t('consultationPassenger.safariInstructions'),
                            tipo: 'info',
                            duracao: 8000
                        });
                        this.installingPWA = false;
                        this.irParaTroubleshootingPWA();
                    }, 2000);
                } else {
                    // Se for Safari, mostrar instruções
                    EventBus.$emit('show-alert', {
                        mensagem: this.$t('consultationPassenger.openSafariInstructions'),
                        tipo: 'info',
                        duracao: 8000
                    });
                    
                    setTimeout(() => {
                        this.installingPWA = false;
                        this.irParaTroubleshootingPWA();
                    }, 3000);
                }
                return;
            }

            // Para outros navegadores, tentar instalação automática
            if (this.deferredPrompt) {
                this.installingPWA = true;

                this.deferredPrompt.prompt();
                this.deferredPrompt.userChoice.then((choiceResult) => {
                    if (choiceResult.outcome === 'accepted') {
                        EventBus.$emit('show-alert', {
                            mensagem: this.$t('consultationPassenger.installSuccess'),
                            tipo: 'success'
                        });
                    } else {
                        EventBus.$emit('show-alert', {
                            mensagem: this.$t('consultationPassenger.installCanceled'),
                            tipo: 'info'
                        });
                    }

                    this.deferredPrompt = null;
                    this.installingPWA = false;
                    this.showPWAInstall = false;
                });
            } else {
                this.installingPWA = true;
                setTimeout(() => {
                    this.installingPWA = false;
                    this.showPWAInstall = false;
                    
                    EventBus.$emit('show-alert', {
                        mensagem: this.$t('consultationPassenger.automaticInstallationNotAvailable'),
                        tipo: 'info'
                    });
                    
                    this.irParaTroubleshootingPWA();
                }, 10000);
            }
        },
        closePWAInstall() {
            this.showPWAInstall = false;
            this.installingPWA = false;
        },
        detectPlatform() {
            // Detectar iOS
            this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

            // Detectar Safari
            this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

            // Detectar se já está em modo standalone (PWA instalado)
            this.isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                window.navigator.standalone === true;

            console.log('Plataforma detectada:', {
                isIOS: this.isIOS,
                isSafari: this.isSafari,
                isStandalone: this.isStandalone,
                userAgent: navigator.userAgent
            });
        },
        setupPWAListeners() {

            // Só adicionar listener para beforeinstallprompt se não for iOS
            if (!this.isIOS) {
                window.addEventListener('beforeinstallprompt', (e) => {
                    e.preventDefault();
                    this.deferredPrompt = e;
                    this.canInstallPWA = true;
                });
            }

            window.addEventListener('appinstalled', () => {
                this.deferredPrompt = null;
                this.canInstallPWA = false;
                this.showPWAInstall = false;
                
                EventBus.$emit('show-alert', {
                    mensagem: this.$t('consultationPassenger.installSuccess'),
                    tipo: 'success'
                });
            });
        },
        getUserLocationLabel() {
            // Return simple label for user location
            return 'Posição do usuário';
        },
        announceUserLocation() {
            // Announce only the address when user location is focused/clicked
            const announcement = this.getUserLocationLabel();
            this.markerClickAnnouncement = announcement;
            
            // Clear after announcement
            setTimeout(() => {
                this.markerClickAnnouncement = '';
            }, 3000);
        },
        announceVehicleClick(veiculo) {
            // Get line name
            const linha = this.linhas.find(l => l.id_linha === veiculo.id_linha);
            const nomeLinha = linha ? linha.nome_linha : 'Linha desconhecida';
            
            // Build announcement
            const status = veiculo.gps_offline ? 'Offline' : 'Online';
            const announcement = `Veículo ${veiculo.cod_veiculo} da ${nomeLinha}. Status: ${status}. Última atualização: ${veiculo.dt_gps}`;
            
            this.markerClickAnnouncement = announcement;
            
            // Clear after announcement
            setTimeout(() => {
                this.markerClickAnnouncement = '';
            }, 3000);
        },
        /** Verdadeiro quando a parada é a destacada no sinótico (selecionada ou mais próxima; usado para tooltip permanente no mapa). */
        isParadaSelecionadaNoSinotico(parada) {
            const ponto = this.paradaParaPontoSelecionadoSinotico;
            if (!ponto || !parada) return false;
            const idA = ponto.id_cerca != null ? ponto.id_cerca : ponto.id_parada;
            const idB = parada.id_cerca != null ? parada.id_cerca : parada.id_parada;
            if (idA == null || idB == null) return false;
            return String(idA) === String(idB);
        },
        /** Verdadeiro quando a parada é a mais próxima (para tooltip permanente no mapa). */
        
        announceStopClick(parada) {
            // Announce stop marker click - simplified
            const announcement = `Parada ${parada.nome_cerca}`;
            
            this.markerClickAnnouncement = announcement;
            
            // Clear after announcement
            setTimeout(() => {
                this.markerClickAnnouncement = '';
            }, 3000);
        },
        announceTimestampClick(veiculo) {
            // Announce timestamp marker click
            const linha = this.linhas.find(l => l.id_linha === veiculo.id_linha);
            const nomeLinha = linha ? linha.nome_linha : 'Linha desconhecida';
            let horaVeiculo = veiculo.dt_gps.split(' ')[1];
            const announcement = `Última atualização do veículo ${veiculo.cod_veiculo} da ${nomeLinha}: ${horaVeiculo}`;
            
            this.markerClickAnnouncement = announcement;
            
            // Clear after announcement
            setTimeout(() => {
                this.markerClickAnnouncement = '';
            }, 3000);
        },
        announceVehicleCardFocus(veiculo) {
            // Announce vehicle card information when it receives focus
            const nomeLinha = this.getNomeLinha(veiculo.id_linha);
            const sentido = this.getSentido(veiculo.sentido);
            const tempoChegada = this.getTempoChegadaParadasPainelCard(veiculo, this.paradaSelecionadaPainel);
            
            let announcement = `${nomeLinha} - ${sentido}. Veículo ${veiculo.cod_veiculo}.`;
            if (veiculo.has_eta == 1) {
                announcement += ` Chegada prevista: ${tempoChegada}.`;
            } else {
                announcement += ` ${tempoChegada}.`;
            }
            announcement += ` Pressione Enter para abrir o mapa.`;
            
            this.markerClickAnnouncement = announcement;
            
            // Clear after announcement
            setTimeout(() => {
                this.markerClickAnnouncement = '';
            }, 3000);
        },
        announceStopSelectFocus() {
            // Announce stop selector when it receives focus
            const announcement = `Seletor de parada. ${this.paradasProximas.length} paradas disponíveis. Parada atual: ${this.paradaSelecionadaPainel ? this.paradaSelecionadaPainel.nome_cerca : 'Nenhuma selecionada'}.`;
            
            this.markerClickAnnouncement = announcement;
            
            // Clear after announcement
            setTimeout(() => {
                this.markerClickAnnouncement = '';
            }, 3000);
        },
        announceStopChange() {
            // Announce when stop selection changes (via arrow keys or click)
            if (this.paradaSelecionadaPainel) {
                const announcement = `${this.$t('consultationPassenger.stopSelected')} ${this.paradaSelecionadaPainel.nome_cerca}`;
                this.markerClickAnnouncement = announcement;
                
                // Clear after announcement
                setTimeout(() => {
                    this.markerClickAnnouncement = '';
                }, 3000);
            }
        },
        announceSinoticoFocus(announcement) {
            // Announce sinoptic point when it receives focus
            this.markerClickAnnouncement = announcement;
            
            // Clear after announcement
            setTimeout(() => {
                this.markerClickAnnouncement = '';
            }, 3000);
        },
        getParadasSinotico() {
            // Filter paradas for sinoptic display
            // Only include regular stops (tipo 5)
            // Terminal stops are handled separately via pontoInicial and pontoFinal props
            const filtered = this.paradas.filter(parada => 
                parada.id_tipo_cerca === 5 && 
                parada.id_sentido == this.sentidoSinotico.id && 
                parada.id_linha == this.idLinhaSelected
            );
            return filtered;
        },
        getVeiculosSinotico() {
            if (!this.idLinhaSelected) return [];
            
            const veiculos = this.veiculosAtivos.filter(veiculo => 
                veiculo.id_linha == this.idLinhaSelected && 
                veiculo.sem_comunicacao == 0 && 
                veiculo.sentido + 1 == this.sentidoSinotico.id && 
                veiculo.id_viagem != null
            );
            
            // Remove duplicatas por id_veiculo
            const veiculosUnicos = new Map();
            veiculos.forEach(v => {
                if (!veiculosUnicos.has(v.id_veiculo)) {
                    veiculosUnicos.set(v.id_veiculo, v);
                }
            });
            
            return Array.from(veiculosUnicos.values());
        }
    }
}
</script>

<style>
/* Screen reader only - visually hidden but accessible */
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

/* Skip link visible on focus for keyboard navigation */
.sr-only-focusable:focus {
    position: absolute;
    top: 0;
    left: 0;
    width: auto;
    height: auto;
    padding: 8px 16px;
    margin: 0;
    overflow: visible;
    clip: auto;
    white-space: normal;
    background-color: #000;
    color: #fff;
    z-index: 9999;
    text-decoration: none;
    border: 2px solid #fff;
}

/* Prevent map controls from being keyboard accessible */
#mapcontainer .leaflet-control-zoom a,
#mapcontainer .leaflet-control a,
#mapcontainer button,
#mapcontainer a {
    pointer-events: auto !important;
}

#mapcontainer .leaflet-control-zoom a:focus,
#mapcontainer .leaflet-control a:focus,
#mapcontainer button:focus,
#mapcontainer a:focus {
    outline: none !important;
}

/* Inert attribute styling - deactivate main content when dialogs are open */
[inert] {
    pointer-events: none !important;
    cursor: default !important;
    user-select: none !important;
    -webkit-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
}

[inert] *,
[inert] *::before,
[inert] *::after {
    pointer-events: none !important;
    cursor: default !important;
    user-select: none !important;
    -webkit-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
}

/* Ensure inert elements can't be focused */
[inert] a,
[inert] button,
[inert] input,
[inert] select,
[inert] textarea,
[inert] [tabindex] {
    pointer-events: none !important;
    cursor: default !important;
}

/* Slightly dim inert content for visual feedback */
[inert] {
    opacity: 0.6;
    filter: grayscale(20%);
}

/* Focus visible (only when using keyboard, not mouse) */
button:focus-visible,
.v-btn:focus-visible,
a:focus-visible,
[role="button"]:focus-visible {
    outline: 3px solid #4285f4 !important;
    outline-offset: 2px !important;
}

/* Focusable map markers - no outline */
.focusable-marker {
    cursor: pointer !important;
    outline: none !important;
}

.focusable-marker:focus {
    outline: none !important;
}

.focusable-marker:focus-visible {
    outline: none !important;
}

.card__title {
    white-space: nowrap;
    word-break: normal;
    overflow: hidden;
    text-overflow: ellipsis;
}

.eta {
    font-weight: bold;
}

#mapcontainer,
#mapcontainer .leaflet-container,
.l-map {
    width: 100% !important;
    height: 50vh !important;
    max-height: 100vh !important;
    padding: 0 !important;
    position: relative;
}

@media (max-width: 600px) {

    #mapcontainer,
    #mapcontainer .leaflet-container,
    .l-map {
        height: 40vh !important;
    }
}

.atrasada {
    color: red;
    font-weight: bold;
}

.linha-card {
    border-radius: 12px;
    height: 80px;
    padding: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: box-shadow 0.2s;
    cursor: pointer;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    width: 100%;
    margin-bottom: 5px;
}

.linha-card:hover {
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

.linha-card:focus {
    outline: 3px solid #4285f4;
    outline-offset: 2px;
    box-shadow: 0 4px 15px rgba(66, 133, 244, 0.4);
    transform: scale(1.02);
}

.linha-card:focus-visible {
    outline: 3px solid #4285f4;
    outline-offset: 2px;
}

.ponto-proximo {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    background-color: #f0f4ff;
    color: #2a2a2a;
    border-left: 4px solid #4285f4;
    padding: 12px 16px;
    padding-right: 28px;
    margin: 16px 0;
    border-radius: 6px;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    font-size: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.splash-slide-enter-active,
.splash-slide-leave-active {
    transition: transform 0.6s cubic-bezier(.55, 0, .1, 1), opacity 0.4s;
}

.splash-slide-enter {
    transform: translateX(0);
    opacity: 1;
}

.splash-slide-leave-to {
    transform: translateX(-100vw);
    opacity: 0;
    pointer-events: none;
}

.eta__available {
    color: #000000;
    font-weight: bold;
    font-size: small;
}

.eta__unavailable {
    color: #4a4a4a;
    font-size: small;
}

.tempo-chegada--no-ponto {
    font-weight: bold;
    color: #000000;
}

.tempo-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.tempo-icon--live {
    animation: tempoPulse 1.5s ease-in-out infinite;
}

@keyframes tempoPulse {
    0% {
        transform: scale(1);
        opacity: 0.7;
    }
    50% {
        transform: scale(1.2);
        opacity: 1;
    }
    100% {
        transform: scale(1);
        opacity: 0.7;
    }
}

.text-red {
    color: red;
    font-weight: bold;
}

.text-green {
    color: green;
    font-weight: bold;
}

.dt__tooltip {
    background-color: #00000050;
    color: white;
    border: none;
    font-size: x-small;
    font-weight: bold;
    padding: 3px;
}

.vehicle-icon-container {
    position: relative;
    display: inline-block;
}

/* Timestamp badge icon - separate from vehicle icon to avoid blink inheritance */
.timestamp-badge-icon {
    animation: none !important;
    -webkit-animation: none !important;
    -moz-animation: none !important;
    -o-animation: none !important;
}

.timestamp-badge-icon.blink {
    animation: none !important;
    -webkit-animation: none !important;
    -moz-animation: none !important;
    -o-animation: none !important;
}

/* Timestamp badge content styling */
.timestamp-badge-content {
    position: absolute !important;
    top: 20px !important;
    left: 20px !important;
    background-color: #4a4a4a !important;
    color: white !important;
    border: none !important;
    border-radius: 6px !important;
    padding: 1px 4px !important;
    font-size: 8px !important;
    font-weight: bold !important;
    white-space: nowrap !important;
    word-break: normal !important;
    max-width: 120px !important;
    overflow: visible !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5) !important;
    z-index: 1000 !important;
    line-height: 1.1 !important;
    display: inline-block !important;
    animation: none !important;
}

/* PWA Install Dialog Styles */
.pwa-install-dialog .v-overlay__content {
    position: fixed !important;
    top: 20px !important;
    right: 20px !important;
    left: auto !important;
    bottom: auto !important;
    transform: none !important;
    background-color: transparent !important;
    border-radius: 10px;
    padding: 0;
    width: auto !important;
    height: auto !important;
    display: block;
    align-items: initial;
    justify-content: initial;
    box-shadow: none;
}

.pwa-install-card {
    background-color: #fff;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    width: 100%;
    max-width: 400px;
    margin: 0;
    animation: slideInFromRight 0.3s ease-out;
}

@keyframes slideInFromRight {
    from {
        transform: translateX(100%);
        opacity: 0;
    }

    to {
        transform: translateX(0);
        opacity: 1;
    }
}

/* Responsive adjustments for mobile */
@media (max-width: 768px) {
    .pwa-install-dialog .v-overlay__content {
        top: 10px !important;
        right: 10px !important;
        left: 10px !important;
        max-width: calc(100vw - 20px) !important;
    }

    .pwa-install-card {
        max-width: 100%;
        margin: 0;
    }
}

@media (max-width: 480px) {
    .pwa-install-dialog .v-overlay__content {
        top: 5px !important;
        right: 5px !important;
        left: 5px !important;
        max-width: calc(100vw - 10px) !important;
    }

    .pwa-install-card .v-card__text {
        padding: 12px !important;
    }

    .pwa-install-card .text-h6 {
        font-size: 1.1em !important;
    }

    .pwa-install-card .text-body-2 {
        font-size: 0.85em !important;
    }
}

/* Card de Parada Mais Próxima */
.parada-proxima-card {
    border-left: 4px solid;
    border-radius: 8px !important;
    transition: all 0.3s ease;
}

.parada-proxima-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}
</style>